import ChatAssistant from './components/ChatAssistant2';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TIMING, API_CONFIG, VALIDATION } from './constants/appConstants';
import { applyCustomerFilters } from './utils/filterUtils';
import { Routes, Route, Navigate } from 'react-router-dom';
import Papa from 'papaparse';
import { theme as themeConstants, colors } from './constants/theme';
import { decodeVIN } from './utils/vinDecoder';
import { aiAssignLead } from './utils/aiAssignment';
import { formatTimestamp, formatConversationTimestamp } from './utils/formatters';
import { getInitials, getStatusConfig } from './utils/customerUtils';
import { customerAPI, analyticsAPI } from './services/api';
import { useCustomers } from './hooks/useCustomers';
import { AnalyticsTab } from './components/Analytics/AnalyticsTab';
import { ManualEntryModal } from './components/Modals/ManualEntryModal';
import { CSVImportModal } from './components/Modals/CSVImportModal';
import { EditCustomerModal } from './components/Modals/EditCustomerModal';
import { CustomerDetailModal } from './components/Modals/CustomerDetailModal';
import { TradeInModal } from './components/Modals/TradeInModal';
import { ScheduleAppointmentModal } from './components/Modals/ScheduleAppointmentModal';
import { CustomerTable } from './components/Customers/CustomerTable';
import MessageInput from './components/Conversations/MessageInput';
import ConversationsTab from './components/Conversations/ConversationsTab';
import { Header } from './components/Layout/Header';
import { TabNavigation } from './components/Layout/TabNavigation';
import { ToastContainer } from './components/Layout/ToastContainer';
import { useToast } from './hooks/useToast';
import { IntegrationsTab } from './components/Settings/IntegrationsTab';
import Homepage from './components/Homepage/Homepage';
import LoginPage from './components/Auth/LoginPage';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import SMSConsent from './pages/SMSConsent';
import SmsOptIn from './pages/SmsOptIn';
import Footer from './components/Footer';
import LoadingScreen from './components/Layout/LoadingScreen';
import { 
  MessageSquare, Users, Car, TrendingUp, CheckCircle, 
  XCircle, AlertCircle, Send, Search, Settings, Zap, DollarSign,
  Calendar, PhoneCall, Activity, Target, Percent,
  ArrowUpRight, UserCheck,
  Smartphone, X, 
  AlertTriangle, Lightbulb, Plus, Trash2, Flame, RefreshCw
} from 'lucide-react';

  // Dashboard component - contains all CRM functionality
// Moved outside App to prevent recreation on every render
const Dashboard = ({
  isDarkMode,
  theme,
  colors,
  isInitialLoad,
  user,
  dealer,
  handleLogout,
  hasInitialLoadCompletedRef,
  notifications,
  setNotifications,
  handleToggleTheme,
  setUser,
  setShowSettingsModal,
  setShowImportModal,
  setShowAppraisalSettings,
  settingsTab,
  setSettingsTab,
  notificationsEnabled,
  setNotificationsEnabled,
  addNotification,
  activeTab,
  handleTabChange,
  dateRange,
  setDateRange,
  analytics,
    customers,
  recentActions,
  tabLoading,
  previousTab,
  conversationsTabDisplayStyle,
  selectedCustomer,
  setSelectedCustomer,
  conversations,
  newMessage,
  handleMessageChange,
  handleSendMessage,
  aiResponding,
  handleToggleAI,
  customerTyping,
  aiPausedCustomers,
  statusFilter,
  setStatusFilter,
  searchQuery,
  setSearchQuery,
  fetchConversation,
  messagesEndRef,
  filteredCustomers,
  customersLoading,
  showToast,
  toasts,
  removeToast,
  showSettingsModal,
  showImportModal,
  showAppraisalSettings,
  appraisalSettings,
  setAppraisalSettings,
  appraisalDateFilter,
  setAppraisalDateFilter,
  showManualEntryModal,
  setShowManualEntryModal,
  manualLeadForm,
  setManualLeadForm,
  handleManualLeadSubmit,
  vinDecoding,
  vinData,
  setVinData,
  handleDecodeVIN,
  showEditModal,
  setShowEditModal,
  editingCustomer,
  setEditingCustomer,
  handleEditSubmit,
  showDetailModal,
  setShowDetailModal,
  detailCustomer,
  handleViewCustomerDetail,
  handleEditCustomer,
  handleDeleteCustomer,
  handleScheduleAppointment,
  handleAddTradeIn,
  handleDeleteTradeIn,
  schedulingCustomer,
  setSchedulingCustomer,
  handleAppointmentScheduled,
  showScheduleModal,
  setShowScheduleModal,
  showTradeInModal,
  setShowTradeInModal,
  tradeInForm,
  setTradeInForm,
  handleTradeInSubmit,
  importFile,
  setImportFile,
  importPreview,
  setImportPreview,
  columnMapping,
  setColumnMapping,
  isImporting,
  handleFileUpload,
  handleBulkImport,
  fetchCustomers
}) => {
  const handleChatAssistantAction = useCallback((action) => {
    if (!action) return;

    switch (action.type) {
      case 'SELECT_CUSTOMER':
      case 'NAVIGATE_TO_CUSTOMER': {
        const customer = customers.find(c => c.id === action.customerId);
        if (customer) {
          handleTabChange('conversations');
          setSelectedCustomer(customer);
          fetchConversation(customer.id);
        }
        break;
      }
      case 'APPLY_FILTER': {
        handleTabChange('conversations');
        setStatusFilter(action.filter);
        break;
      }
      case 'OPEN_SCHEDULE_MODAL': {
        const customerToSchedule = customers.find(c => c.id === action.customerId);
        if (customerToSchedule) {
          handleTabChange('conversations');
          setSchedulingCustomer(customerToSchedule);
    setShowScheduleModal(true);
        }
        break;
      }
      case 'OPEN_DETAIL_MODAL': {
        const customerDetail = customers.find(c => c.id === action.customerId);
        if (customerDetail) {
          handleViewCustomerDetail(customerDetail);
        }
        break;
      }
      default:
        console.log('Unknown action type:', action.type);
    }
  }, [
    customers,
    handleTabChange,
    setSelectedCustomer,
    fetchConversation,
    setStatusFilter,
    setSchedulingCustomer,
    setShowScheduleModal,
    handleViewCustomerDetail
  ]);

  return (
    <div style={{
      minHeight: '100vh',
      background: isDarkMode 
        ? '#000000'
        : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      backgroundImage: isDarkMode
        ? `
          radial-gradient(ellipse 80% 50% at 50% -20%, rgba(59, 130, 246, 0.15), transparent),
          radial-gradient(ellipse 60% 40% at 100% 100%, rgba(59, 130, 246, 0.1), transparent),
          radial-gradient(ellipse 40% 60% at 0% 100%, rgba(99, 102, 241, 0.08), transparent)
        `
        : 'none',
      position: 'relative',
      overflow: 'hidden',
      color: theme.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", sans-serif'
    }}>
      {/* Loading Screen - Show ONLY during initial load after login, never when switching tabs */}
      {isInitialLoad && (dealer || user) && !hasInitialLoadCompletedRef.current && <LoadingScreen />}
      {/* Animated background orbs */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '20%',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: isDarkMode 
          ? 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)'
          : 'radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%)',
        filter: 'blur(60px)',
        animation: 'float 20s ease-in-out infinite',
        pointerEvents: 'none'
      }}></div>
      <div style={{
        position: 'absolute',
        bottom: '20%',
        right: '15%',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: isDarkMode 
          ? 'radial-gradient(circle, rgba(168, 85, 247, 0.15) 0%, transparent 70%)'
          : 'radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%)',
        filter: 'blur(80px)',
        animation: 'float 25s ease-in-out infinite reverse',
        pointerEvents: 'none'
      }}></div>

      <style>{`
        * {
          transition: background 1.6s cubic-bezier(0.16, 1, 0.3, 1),
                      background-color 1.6s cubic-bezier(0.16, 1, 0.3, 1),
                      color 1.6s cubic-bezier(0.16, 1, 0.3, 1),
                      border-color 1.6s cubic-bezier(0.16, 1, 0.3, 1),
                      box-shadow 1.6s cubic-bezier(0.16, 1, 0.3, 1) !important;
        }

        
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.8;
          }
        }
        
        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 5px currentColor;
          }
          50% {
            box-shadow: 0 0 20px currentColor, 0 0 30px currentColor;
          }
        }
        
        .pulse-on-update {
          animation: pulse 0.6s ease-out;
        }
        
        .hover-lift {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        
        .hover-lift:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
        }
        
        .glow-border {
          position: relative;
          overflow: hidden;
        }
        
        .glow-border::before {
          content: '';
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          background: linear-gradient(45deg, #3b82f6, #8b5cf6, #ec4899, #3b82f6);
          border-radius: inherit;
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: -1;
          background-size: 300% 300%;
          animation: gradientShift 3s ease infinite;
        }
        
        .glow-border:hover::before {
          opacity: 0.7;
        }
        
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        
        
        .tilt-card {
          transform-style: preserve-3d;
          transition: transform 0.1s ease-out;
        }
        
        .tilt-card:hover {
          transform: perspective(1000px) rotateX(var(--tilt-x, 0deg)) rotateY(var(--tilt-y, 0deg)) scale3d(1.02, 1.02, 1.02);
        }

        @keyframes fadeInStats {
          0% {
            opacity: 0;
          }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
          100% {
            opacity: 1;
          }
        }
        
        .stats-loading {
          animation: fadeInStats 0.8s ease-out;
        }

        @keyframes statsSlideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .stat-card {
          animation: statsSlideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) backwards;
        }
        
        .stat-card:nth-child(1) { animation-delay: 0.1s; }
        .stat-card:nth-child(2) { animation-delay: 0.2s; }
        .stat-card:nth-child(3) { animation-delay: 0.3s; }
        .stat-card:nth-child(4) { animation-delay: 0.4s; }

        @keyframes toastSlideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .toast {
          animation: toastSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes tabFadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .tab-content {
          transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
          will-change: auto;
          contain: layout style paint;
        }
        
        .tab-content.tab-fade-in {
          opacity: 1;
          transform: translateY(0);
          animation: tabFadeIn 0.3s ease-in-out;
        }
        
        .tab-content.tab-fade-out {
          opacity: 0;
          transform: translateY(10px);
          transition: opacity 0.15s ease-in-out, transform 0.15s ease-in-out;
        }
        
        /* Prevent visual flash during React.StrictMode remounts */
        .tab-content > * {
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          transform: translateZ(0);
          -webkit-transform: translateZ(0);
        }
        
        .tab-loading-overlay {
          animation: fadeIn 0.15s ease-in-out;
        }
        
        .tab-loading-bar {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
          animation: loadingBar 0.2s ease-in-out;
          z-index: 100;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes loadingBar {
          from {
            transform: scaleX(0);
            transform-origin: left;
          }
          to {
            transform: scaleX(1);
            transform-origin: left;
          }
        }
        
        @keyframes typingPulse {
          0%, 60%, 100% {
            opacity: 0.3;
            transform: translateY(0) scale(0.8);
          }
          30% {
            opacity: 1;
            transform: translateY(-4px) scale(1);
          }
        }
        
        .typing-dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.9);
          animation: typingPulse 1.4s infinite ease-in-out;
        }
        
        .typing-dot-1 {
          animation-delay: 0s;
        }
        
        .typing-dot-2 {
          animation-delay: 0.2s;
        }
        
        .typing-dot-3 {
          animation-delay: 0.4s;
        }
        
        .typing-indicator-container {
          animation: typingFadeIn 0.3s ease-out;
        }
        
        @keyframes typingFadeIn {
          from {
            opacity: 0;
            transform: translateY(5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(2deg); }
        }
        
        @keyframes borderGlow {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        
        .login-loader {
          position: fixed;
          inset: 0;
          background: linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #2d1b4e 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: fadeIn 0.3s ease-out;
        }
        
        .loader-logo {
          width: 140px;
          height: 140px;
          background: rgba(26, 31, 46, 0.6);
          backdrop-filter: blur(30px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 35px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 80px rgba(59, 130, 246, 0.3);
          animation: logoFloat 6s ease-in-out infinite;
          margin-bottom: 24px;
        }
        
        .loader-logo::before {
          content: '';
          position: absolute;
          inset: -2px;
          border-radius: 35px;
          padding: 2px;
          background: linear-gradient(135deg, #667eea, #764ba2, #f093fb);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          opacity: 0.6;
          animation: borderGlow 3s ease-in-out infinite;
        }
        
        .loader-text {
          font-size: 52px;
          font-weight: 900;
          background: linear-gradient(135deg, #667eea 0%, #a78bfa 50%, #f093fb 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          letter-spacing: -3px;
          position: relative;
          z-index: 1;
        }
        
        .loader-message {
          color: rgba(255, 255, 255, 0.6);
          font-size: 16px;
          font-weight: 500;
          letter-spacing: 1px;
        }

        @keyframes float {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(50px, 50px); }
        }
      `}</style>

      <Header
        user={dealer || user}
        notifications={notifications}
        onClearNotifications={() => setNotifications([])}
        isDarkMode={isDarkMode}
        onToggleTheme={handleToggleTheme}
        onOpenSettings={() => setShowSettingsModal(true)}
        onLogout={handleLogout}
        onImportCSV={() => setShowImportModal(true)}
        onOpenAppraisalSettings={() => setShowAppraisalSettings(true)}
        theme={theme}
        colors={colors}
        notificationsEnabled={notificationsEnabled}
      />

      {showSettingsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={() => setShowSettingsModal(false)}>
          <div style={{
            background: theme.bgSecondary || '#1a1f2e',
            borderRadius: '16px',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            width: '100%',
            maxWidth: settingsTab === 'integrations' ? '1200px' : '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
          }}
          onClick={(e) => e.stopPropagation()}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: theme.text }}>Settings</h2>
              <button
                onClick={() => setShowSettingsModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: theme.textSecondary,
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Tab Navigation */}
            <div style={{
              display: 'flex',
              gap: '8px',
              margin: '20px 24px 0 24px',
              borderBottom: '2px solid rgba(59, 130, 246, 0.2)',
              paddingBottom: '0'
            }}>
              <button
                onClick={() => setSettingsTab('notifications')}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px 8px 0 0',
                  border: 'none',
                  borderBottom: settingsTab === 'notifications' 
                    ? `3px solid ${colors.primary}`
                    : '3px solid transparent',
                  background: settingsTab === 'notifications' 
                    ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`
                    : 'transparent',
                  color: settingsTab === 'notifications' ? '#fff' : theme.textSecondary,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '-2px'
                }}
                onMouseOver={(e) => {
                  if (settingsTab !== 'notifications') {
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                    e.currentTarget.style.color = theme.text;
                  }
                }}
                onMouseOut={(e) => {
                  if (settingsTab !== 'notifications') {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = theme.textSecondary;
                  }
                }}
              >
                Notifications
              </button>
              <button
                onClick={() => setSettingsTab('integrations')}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px 8px 0 0',
                  border: 'none',
                  borderBottom: settingsTab === 'integrations' 
                    ? `3px solid ${colors.primary}`
                    : '3px solid transparent',
                  background: settingsTab === 'integrations' 
                    ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`
                    : 'transparent',
                  color: settingsTab === 'integrations' ? '#fff' : theme.textSecondary,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '-2px'
                }}
                onMouseOver={(e) => {
                  if (settingsTab !== 'integrations') {
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                    e.currentTarget.style.color = theme.text;
                  }
                }}
                onMouseOut={(e) => {
                  if (settingsTab !== 'integrations') {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = theme.textSecondary;
                  }
                }}
              >
                Integrations
              </button>
            </div>

            {/* Tab Content */}
            <div style={{ padding: settingsTab === 'integrations' ? '0' : '24px' }}>
              {settingsTab === 'notifications' && (
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: theme.text }}>
                    Notification Preferences
                  </h3>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      background: 'rgba(59, 130, 246, 0.05)',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={notificationsEnabled}
                        onChange={(e) => setNotificationsEnabled(e.target.checked)}
                        style={{ width: '18px', height: '18px' }}
                      />
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: theme.text }}>Enable Notifications</div>
                        <div style={{ fontSize: '12px', color: theme.textSecondary }}>
                          Receive in-app notifications for important events
                        </div>
                      </div>
                    </label>
                  </div>

                  <button
                    onClick={() => {
                      setShowSettingsModal(false);
                      addNotification({
                        id: Date.now(),
                        type: 'settings',
                        message: '✅ Settings saved successfully!',
                        timestamp: new Date().toISOString()
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginTop: '24px'
                    }}
                  >
                    Save Settings
                  </button>
                </div>
              )}

              {settingsTab === 'integrations' && (
                <IntegrationsTab 
                  theme={theme} 
                  colors={colors} 
                  showToast={showToast} 
                />
              )}
            </div>
          </div>
        </div>
      )}

      <TabNavigation
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        theme={theme}
        colors={colors}
        appointmentCount={customers.filter(c => c.acquisitionStatus === 'appointment-set').length}
        hotOpportunityCount={customers.filter(c => c.acquisitionStatus === 'hot-opportunity' || c.tradeInInterest).length}
      />

      <div style={{ padding: '24px' }}>
        {activeTab === 'dashboard' && (
          <div className="tab-content">
            <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '14px', color: theme.textSecondary }}>Showing data for:</span>
              {['7days', '30days', '90days'].map(range => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  style={{
                    padding: '8px 16px',
                    background: dateRange === range ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)` : 'rgba(59, 130, 246, 0.1)',
                    border: dateRange === range ? 'none' : '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '8px',
                    color: theme.text,
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {range === '7days' ? 'Last 7 Days' : range === '30days' ? 'Last 30 Days' : 'Last 90 Days'}
                </button>
              ))}
            </div>

            <div className="stats-loading" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px',
              marginBottom: '24px'
            }}>
              {[
                { 
                  label: 'Total Conversations', 
                  value: analytics.totalConversations || 0, 
                  icon: MessageSquare, 
                  color: colors.primary, 
                  trend: '+12%' 
                },
                { 
                  label: 'Acquisition Rate', 
                  value: `${analytics.acquisitionRate || 0}%`, 
                  icon: Target, 
                  color: '#10b981', 
                  trend: '+8%' 
                },
                { 
                  label: 'Response Rate', 
                  value: `${analytics.responseRate || 0}%`, 
                  icon: Activity, 
                  color: '#f59e0b', 
                  trend: '+15%' 
                },
                { 
                  label: 'Avg Offer Value', 
                  value: `$${(analytics.avgOfferValue || 0).toLocaleString()}`, 
                  icon: DollarSign, 
                  color: '#8b5cf6', 
                  trend: '+5%' 
                }
              ].map((metric) => (
                <div 
                  key={metric.label}
                  className="stat-card tilt-card"
                  onMouseMove={(e) => {
                    const card = e.currentTarget;
                    const rect = card.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const y = e.clientY - rect.top;
                    const centerX = rect.width / 2;
                    const centerY = rect.height / 2;
                    const rotateX = ((y - centerY) / centerY) * -15;
                    const rotateY = ((x - centerX) / centerX) * 15;
                    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-4px) scale3d(1.02, 1.02, 1.02)`;
                  }}
                  onMouseLeave={(e) => {
                    const card = e.currentTarget;
                    card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0) scale3d(1, 1, 1)';
                  }}
                  style={{
                    background: theme.glass.background,
                backdropFilter: theme.glass.backdropFilter,
                WebkitBackdropFilter: theme.glass.WebkitBackdropFilter,
                boxShadow: theme.glass.boxShadow,
                padding: '24px',
                    borderRadius: '16px',
                    border: `1px solid ${metric.color}40`,
                    transition: 'all 0.3s',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.boxShadow = `0 12px 32px ${metric.color}40`;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span style={{ fontSize: '14px', color: theme.textSecondary, fontWeight: '500' }}>
                      {metric.label}
                    </span>
                    <metric.icon size={20} style={{ color: metric.color }} />
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: theme.text, marginBottom: '4px' }}>
                    {metric.value}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#10b981', fontSize: '13px' }}>
                    <ArrowUpRight size={16} />
                    <span>{metric.trend} vs last period</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              background: theme.glass.background,
                backdropFilter: theme.glass.backdropFilter,
                WebkitBackdropFilter: theme.glass.WebkitBackdropFilter,
                boxShadow: theme.glass.boxShadow,
                padding: '24px',
              borderRadius: '16px',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              marginBottom: '24px'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Activity size={20} style={{ color: colors.primary }} />
                Conversion Funnel
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {[
                  { label: 'Contacted', value: analytics.conversionFunnel?.contacted || 0, color: '#3b82f6' },
                  { label: 'Responded', value: analytics.conversionFunnel?.responded || 0, color: '#8b5cf6' },
                  { label: 'Interested', value: analytics.conversionFunnel?.interested || 0, color: '#f59e0b' },
                  { label: 'Appointment', value: analytics.conversionFunnel?.appointmentSet || 0, color: '#10b981' },
                  { label: 'Completed', value: analytics.conversionFunnel?.completed || 0, color: '#14b8a6' }
                ].map((stage, index, arr) => (
                  <React.Fragment key={stage.label}>
                    <div style={{
                      flex: 1,
                      background: `linear-gradient(135deg, ${stage.color}20 0%, ${stage.color}10 100%)`,
                      border: `2px solid ${stage.color}40`,
                      borderRadius: '12px',
                      padding: '20px',
                      textAlign: 'center',
                      transition: 'all 0.3s',
                      cursor: 'pointer'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.borderColor = stage.color;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.borderColor = `${stage.color}40`;
                    }}>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: stage.color, marginBottom: '8px' }}>
                        {stage.value}
                      </div>
                      <div style={{ fontSize: '13px', color: theme.textSecondary, fontWeight: '500' }}>
                        {stage.label}
                      </div>
                      {index < arr.length - 1 && stage.value > 0 && (
                        <div style={{ fontSize: '11px', color: stage.color, marginTop: '4px' }}>
                          {Math.round((arr[index + 1].value / stage.value) * 100)}% →
                        </div>
                      )}
                    </div>
                    {index < arr.length - 1 && (
                      <div style={{ fontSize: '24px', color: theme.textSecondary }}>→</div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div style={{
              background: theme.glass.background,
                backdropFilter: theme.glass.backdropFilter,
                WebkitBackdropFilter: theme.glass.WebkitBackdropFilter,
                boxShadow: theme.glass.boxShadow,
                padding: '24px',
              borderRadius: '16px',
              border: '1px solid rgba(59, 130, 246, 0.2)'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Zap size={20} style={{ color: colors.primary }} />
                Recent AI Actions
              </h3>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {recentActions.length === 0 ? (
                  <p style={{ color: theme.textSecondary, textAlign: 'center', padding: '20px' }}>
                    No recent actions
                  </p>
                ) : (
                  recentActions.map((action, index) => (
                    <div
                      key={action.id || action.timestamp || `action-${index}`}
                      style={{
                        padding: '12px',
                        borderBottom: index < recentActions.length - 1 ? '1px solid rgba(59, 130, 246, 0.1)' : 'none',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ display: 'flex', alignItems: 'start', gap: '10px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          background: `linear-gradient(135deg, ${colors.primary}20 0%, ${colors.primary}10 100%)`,
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          {action.type === 'message' && <MessageSquare size={16} style={{ color: colors.primary }} />}
                          {action.type === 'offer' && <DollarSign size={16} style={{ color: colors.primary }} />}
                          {action.type === 'appointment' && <Calendar size={16} style={{ color: colors.primary }} />}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontSize: '13px', lineHeight: '1.5', marginBottom: '4px' }}>
                            {action.description}
                          </p>
                          <p style={{ fontSize: '11px', color: theme.textSecondary }}>
                            {formatTimestamp(action.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className={`tab-content ${tabLoading ? 'tab-fade-out' : 'tab-fade-in'}`} style={{ position: 'relative' }}>
            {tabLoading && previousTab && (
              <>
                <div className="tab-loading-bar" />
                <div className="tab-loading-overlay" style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(10, 14, 39, 0.8)',
                  backdropFilter: 'blur(4px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: '16px'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                    color: colors.primary
                  }}>
                    <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>Loading...</span>
                  </div>
                </div>
              </>
            )}
            <AnalyticsTab
              customers={customers}
              theme={theme}
              colors={colors}
              dateRange={dateRange}
              setDateRange={setDateRange}
              showToast={showToast}
            />
          </div>
        )}

        <div style={conversationsTabDisplayStyle}>
          <ConversationsTab
            customers={customers}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
            conversations={conversations}
            newMessage={newMessage}
            handleMessageChange={handleMessageChange}
            handleSendMessage={handleSendMessage}
            aiResponding={aiResponding}
            customerTyping={customerTyping}
            aiPaused={selectedCustomer ? (aiPausedCustomers[selectedCustomer.id] ?? selectedCustomer.aiPaused) : false}
            onToggleAI={handleToggleAI}
            theme={theme}
            colors={colors}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            fetchConversation={fetchConversation}
            messagesEndRef={messagesEndRef}
            isDarkMode={isDarkMode}
            tabLoading={tabLoading}
            previousTab={previousTab}
          />
        </div>

        {activeTab === 'appraisals' && (() => {
          const appointmentCustomers = customers.filter(c => 
            c.acquisitionStatus === 'appointment-set'
          );

          const formatAppointmentDateTime = (date, time) => {
            if (!date) return 'Not scheduled';
    
            try {
              // Just display the date and time as-is, no complex parsing
              const dateObj = new Date(date);
              const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
              const monthDay = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
              // Use time as-is if provided, otherwise show just the date
              const timeStr = time || 'Time TBD';
    
              return `${dayName}, ${monthDay} at ${timeStr}`;
            } catch (e) {
              return date && time ? `${date} at ${time}` : 'Not scheduled';
            }
          };

          const filterByDate = (customersList) => {
            if (appraisalDateFilter === 'all') return customersList;
            
            const now = new Date();
            const today = now.toISOString().split('T')[0];
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);
            const tomorrowStr = tomorrow.toISOString().split('T')[0];
            const weekFromNow = new Date(now);
            weekFromNow.setDate(weekFromNow.getDate() + 7);
            const weekStr = weekFromNow.toISOString().split('T')[0];
            const monthFromNow = new Date(now);
            monthFromNow.setMonth(monthFromNow.getMonth() + 1);
            const monthStr = monthFromNow.toISOString().split('T')[0];
            
            return customersList.filter(customer => {
              if (!customer.appointmentDate) return false;
              
              switch(appraisalDateFilter) {
                case 'today':
                  return customer.appointmentDate === today;
                case 'tomorrow':
                  return customer.appointmentDate === tomorrowStr;
                case 'this-week':
                  return customer.appointmentDate >= today && customer.appointmentDate <= weekStr;
                case 'this-month':
                  return customer.appointmentDate >= today && customer.appointmentDate <= monthStr;
                default:
                  return true;
              }
            });
          };

          const filteredAppointments = filterByDate(appointmentCustomers);
          const groupedByAppraiser = {};
          filteredAppointments.forEach(customer => {
            const appraiser = customer.assignedAppraiser || 'Unassigned';
            if (!groupedByAppraiser[appraiser]) {
              groupedByAppraiser[appraiser] = [];
            }
            groupedByAppraiser[appraiser].push(customer);
          });

          Object.keys(groupedByAppraiser).forEach(appraiser => {
            groupedByAppraiser[appraiser].sort((a, b) => {
              const dateA = new Date(`${a.appointmentDate}T${a.appointmentTime}`);
              const dateB = new Date(`${b.appointmentDate}T${b.appointmentTime}`);
              return dateA - dateB;
            });
          });

          return (
            <div className="tab-content">
              <div style={{
                background: theme.glass.background,
                backdropFilter: theme.glass.backdropFilter,
                WebkitBackdropFilter: theme.glass.WebkitBackdropFilter,
                boxShadow: theme.glass.boxShadow,
                padding: '24px',
                borderRadius: '16px',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div>
                    <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Calendar size={28} style={{ color: colors.primary }} />
                      Scheduled Appraisals
                    </h2>
                    <p style={{ color: theme.textSecondary, fontSize: '14px' }}>
                      {filteredAppointments.length} appointment{filteredAppointments.length !== 1 ? 's' : ''} scheduled
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'today', label: 'Today' },
                    { id: 'tomorrow', label: 'Tomorrow' },
                    { id: 'this-week', label: 'This Week' },
                    { id: 'this-month', label: 'This Month' }
                  ].map(filter => {
                    let count = 0;
                    if (filter.id === 'all') {
                      count = appointmentCustomers.length;
                    } else {
                      const now = new Date();
                      const today = now.toISOString().split('T')[0];
                      const tomorrow = new Date(now);
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      const tomorrowStr = tomorrow.toISOString().split('T')[0];
                      const weekFromNow = new Date(now);
                      weekFromNow.setDate(weekFromNow.getDate() + 7);
                      const weekStr = weekFromNow.toISOString().split('T')[0];
                      const monthFromNow = new Date(now);
                      monthFromNow.setMonth(monthFromNow.getMonth() + 1);
                      const monthStr = monthFromNow.toISOString().split('T')[0];
                      
                      switch(filter.id) {
                        case 'today':
                          count = appointmentCustomers.filter(c => c.appointmentDate === today).length;
                          break;
                        case 'tomorrow':
                          count = appointmentCustomers.filter(c => c.appointmentDate === tomorrowStr).length;
                          break;
                        case 'this-week':
                          count = appointmentCustomers.filter(c => c.appointmentDate >= today && c.appointmentDate <= weekStr).length;
                          break;
                        case 'this-month':
                          count = appointmentCustomers.filter(c => c.appointmentDate >= today && c.appointmentDate <= monthStr).length;
                          break;
                        default:
                          count = appointmentCustomers.length;
                      }
                    }
                    return (
                      <button
                        key={filter.id}
                        onClick={() => setAppraisalDateFilter(filter.id)}
                        style={{
                          padding: '8px 16px',
                          background: appraisalDateFilter === filter.id 
                            ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`
                            : 'rgba(255, 255, 255, 0.1)',
                          border: appraisalDateFilter === filter.id 
                            ? 'none' 
                            : '1px solid rgba(59, 130, 246, 0.2)',
                          borderRadius: '8px',
                          color: appraisalDateFilter === filter.id ? 'white' : theme.text,
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {filter.label} ({count})
                      </button>
                    );
                  })}
                </div>
              </div>

              {filteredAppointments.length === 0 ? (
                <div style={{
                  padding: '60px 20px',
                  textAlign: 'center',
                  color: theme.textSecondary,
                  background: theme.cardBg,
                  borderRadius: '16px',
                  border: '1px solid rgba(59, 130, 246, 0.2)'
                }}>
                  <Calendar size={64} style={{ opacity: 0.3, marginBottom: '16px' }} />
                  <p style={{ fontSize: '16px' }}>No appointments scheduled</p>
                </div>
              ) : (
                Object.entries(groupedByAppraiser).map(([appraiser, appraiserAppointments]) => (
                  <div key={appraiser} style={{ marginBottom: '32px' }}>
                    <div style={{
                      background: theme.glass.background,
                      backdropFilter: theme.glass.backdropFilter,
                      WebkitBackdropFilter: theme.glass.WebkitBackdropFilter,
                      padding: '16px 20px',
                      borderRadius: '12px',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      marginBottom: '16px'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: theme.text,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <UserCheck size={18} color={colors.primary} />
                        {appraiser} ({appraiserAppointments.length})
                      </h3>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {appraiserAppointments.map(customer => {
                        const customerName = customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
                        const vehicle = customer.vehicle || `${customer.vehicleYear || ''} ${customer.vehicleMake || ''} ${customer.vehicleModel || ''}`.trim();
                        const phone = customer.phone || customer.cPhone || 'Not provided';

                        return (
                          <div
                            key={customer.id}
                            style={{
                              background: theme.glass.background,
                              backdropFilter: theme.glass.backdropFilter,
                              WebkitBackdropFilter: theme.glass.WebkitBackdropFilter,
                              boxShadow: theme.glass.boxShadow,
                              padding: '20px',
                              borderRadius: '12px',
                              border: '1px solid rgba(59, 130, 246, 0.2)',
                              transition: 'all 0.3s',
                              cursor: 'pointer'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.transform = 'translateX(4px)';
                              e.currentTarget.style.boxShadow = `0 8px 24px ${colors.primary}20`;
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.transform = 'translateX(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                            onClick={() => handleViewCustomerDetail(customer)}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                              <div style={{
                                width: '56px',
                                height: '56px',
                                borderRadius: '12px',
                                background: `linear-gradient(135deg, ${colors.primary}40 0%, ${colors.secondary}40 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px',
                                fontWeight: '700',
                                color: colors.primary,
                                flexShrink: 0
                              }}>
                                {getInitials(customerName)}
                              </div>

                              <div style={{ flex: 1 }}>
                                <div style={{ marginBottom: '8px' }}>
                                  <h4 style={{
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: theme.text,
                                    marginBottom: '4px'
                                  }}>
                                    {customerName}
                                  </h4>
                                  <div style={{
                                    fontSize: '14px',
                                    color: theme.textSecondary,
                                    marginBottom: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    flexWrap: 'wrap'
                                  }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <Car size={14} />
                                      {vehicle}
                                    </span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <PhoneCall size={14} />
                                      {phone}
                                    </span>
                                  </div>
                                </div>

                                <div style={{
                                  fontSize: '15px',
                                  fontWeight: '600',
                                  color: colors.primary,
                                  marginBottom: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  <Calendar size={16} />
                                  {formatAppointmentDateTime(customer.appointmentDate, customer.appointmentTime)}
                                </div>

                                <div style={{
                                  fontSize: '13px',
                                  color: theme.textSecondary,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  <UserCheck size={14} />
                                  Assigned to: {customer.assignedAppraiser}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          );
        })()}

        {activeTab === 'hot-leads' && (() => {
          const hotOpportunities = customers.filter(c => 
            c.acquisitionStatus === 'hot-opportunity' || c.tradeInInterest === true
          );

          return (
            <div className="tab-content">
              <div style={{
                background: theme.glass.background,
                  backdropFilter: theme.glass.backdropFilter,
                  WebkitBackdropFilter: theme.glass.WebkitBackdropFilter,
                  boxShadow: theme.glass.boxShadow,
                  padding: '24px',
                borderRadius: '16px',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                marginBottom: '24px'
              }}>
                <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Flame size={28} style={{ color: '#ef4444' }} />
                  🔥 Hot Opportunities
                </h2>
                <p style={{ color: theme.textSecondary, fontSize: '14px' }}>
                  {hotOpportunities.length} customer{hotOpportunities.length !== 1 ? 's' : ''} with trade-in interest detected by AI
                </p>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '20px'
              }}>
                {hotOpportunities.length === 0 ? (
                  <div style={{
                    gridColumn: '1 / -1',
                    padding: '60px 20px',
                    textAlign: 'center',
                    color: theme.textSecondary
                  }}>
                    <Flame size={64} style={{ opacity: 0.3, marginBottom: '16px' }} />
                    <p style={{ fontSize: '16px' }}>No hot opportunities yet</p>
                    <p style={{ fontSize: '14px', marginTop: '8px', opacity: 0.7 }}>
                      Customers with trade-in interest will appear here automatically
                    </p>
                  </div>
                ) : (
                  hotOpportunities.map(customer => (
                  <div
                    key={customer.id}
                    style={{
                      background: theme.cardBg,
                      padding: '20px',
                      borderRadius: '16px',
                      border: '2px solid rgba(16, 185, 129, 0.3)',
                      transition: 'all 0.3s',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(16, 185, 129, 0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      fontSize: '24px'
                    }}>
                      🔥
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '14px',
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0.1) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#10b981'
                      }}>
                        {getInitials(customer.name)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '17px', fontWeight: '600', marginBottom: '2px' }}>
                          {customer.name}
                        </h3>
                        <p style={{ fontSize: '13px', color: theme.textSecondary }}>
                          {customer.phone}
                        </p>
                      </div>
                    </div>

                    <div style={{
                      padding: '12px',
                      background: 'rgba(16, 185, 129, 0.1)',
                      borderRadius: '10px',
                      marginBottom: '12px'
                    }}>
                      <p style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '4px' }}>
                        <Car size={16} style={{ display: 'inline-block', marginRight: '6px', verticalAlign: 'middle' }} /> {customer.vehicle}
                      </p>
                      <p style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'monospace' }}>
                        VIN: {customer.vin}
                      </p>
                    </div>

                    <div style={{
                      padding: '16px',
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)',
                      borderRadius: '10px',
                      textAlign: 'center'
                    }}>
                      <p style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '4px' }}>
                        <DollarSign size={16} style={{ display: 'inline-block', marginRight: '6px', verticalAlign: 'middle' }} /> Offer Amount
                      </p>
                      <p style={{ fontSize: '26px', fontWeight: '700', color: '#10b981' }}>
                        {customer.currentOffer}
                      </p>
                    </div>

                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewCustomerDetail(customer);
                        }}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: 'rgba(59, 130, 246, 0.2)',
                          border: '1px solid rgba(59, 130, 246, 0.5)',
                          borderRadius: '8px',
                          color: colors.primary,
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        View Details
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCustomer(customer);
                          handleTabChange('conversations');
                          fetchConversation(customer.id);
                        }}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                          border: 'none',
                          borderRadius: '8px',
                          color: 'white',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        View Messages
                      </button>
                    </div>
                  </div>
                ))
              )}
              </div>
            </div>
          );
        })()}

        {activeTab === 'all-customers' && (
          <div className={`tab-content ${tabLoading ? 'tab-fade-out' : 'tab-fade-in'}`} style={{ position: 'relative' }}>
            {tabLoading && previousTab && (
              <>
                <div className="tab-loading-bar" />
                <div className="tab-loading-overlay" style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(10, 14, 39, 0.8)',
                  backdropFilter: 'blur(4px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: '16px'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                    color: colors.primary
                  }}>
                    <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>Loading...</span>
                  </div>
                </div>
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
              <button
                onClick={async () => {
                  if (window.confirm('⚠️ WARNING: This will delete ALL customers permanently. Are you absolutely sure?')) {
                    try {
                      const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                      const authToken = localStorage.getItem('token') || localStorage.getItem('vincentrix_token') || null;
                      const response = await fetch(`${BACKEND_URL}/api/customers/clear-all`, { 
                        method: 'POST',
                        headers: { 
                          'Content-Type': 'application/json',
                          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
                        }
                      });
                      
                      if (response.ok) {
                        alert('✅ All customers deleted');
                        window.location.reload();
                      } else {
                        alert('❌ Error deleting customers');
                      }
                    } catch (error) {
                      console.error('Error:', error);
                      alert('❌ Error: ' + error.message);
                    }
                  }
                }}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                🗑️ Clear All Customers
              </button>
            </div>
            <CustomerTable
              customers={filteredCustomers}
              onViewDetails={handleViewCustomerDetail}
              onEdit={handleEditCustomer}
              onDelete={handleDeleteCustomer}
              onSchedule={handleScheduleAppointment}
              onAddNew={() => setShowManualEntryModal(true)}
              theme={theme}
              colors={colors}
              filters={{
                searchQuery,
                setSearchQuery,
                statusFilter,
                setStatusFilter
              }}
              customersLoading={customersLoading}
            />
          </div>
        )}
        {/* Toast Notifications */}
        <ToastContainer 
          toasts={toasts} 
          onRemove={removeToast} 
          theme={theme} 
          colors={colors} 
        />

        {/* Manual Lead Entry Modal */}
        <ManualEntryModal
          show={showManualEntryModal}
          onClose={() => {
            setShowManualEntryModal(false);
            setVinData(null);
          }}
          formData={manualLeadForm}
          setFormData={setManualLeadForm}
          onSubmit={handleManualLeadSubmit}
          vinDecoding={vinDecoding}
          vinData={vinData}
          onDecodeVIN={handleDecodeVIN}
          theme={theme}
          colors={colors}
        />

      {/* Edit Modal */}
      <EditCustomerModal
        show={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingCustomer(null);
        }}
        customer={editingCustomer}
        setCustomer={setEditingCustomer}
        onSave={handleEditSubmit}
        theme={theme}
        colors={colors}
      />

      {/* Detail Modal */}
      <CustomerDetailModal
        show={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        customer={detailCustomer}
        onEdit={handleEditCustomer}
        onDelete={handleDeleteCustomer}
        onAddTradeIn={handleAddTradeIn}
        onDeleteTradeIn={handleDeleteTradeIn}
        theme={theme}
        colors={colors}
        showToast={showToast}
      />

      {/* Trade-In Modal */}
      <ScheduleAppointmentModal
        show={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setSchedulingCustomer(null);
        }}
        customer={schedulingCustomer}
        onSuccess={handleAppointmentScheduled}
        theme={theme}
        colors={colors}
      />

      <TradeInModal
        show={showTradeInModal}
        onClose={() => setShowTradeInModal(false)}
        formData={tradeInForm}
        setFormData={setTradeInForm}
        onSubmit={handleTradeInSubmit}
        theme={theme}
        colors={colors}
      />

      {/* CSV Import Modal */}
      <CSVImportModal
        show={showImportModal}
        onClose={() => {
          setShowImportModal(false);
          setImportFile(null);
          setImportPreview([]);
        }}
        importFile={importFile}
        setImportFile={setImportFile}
        importPreview={importPreview}
        setImportPreview={setImportPreview}
        columnMapping={columnMapping}
        setColumnMapping={setColumnMapping}
        isImporting={isImporting}
        onFileUpload={handleFileUpload}
        onImport={handleBulkImport}
        theme={theme}
        colors={colors}
      />

      {/* Appraisal Settings Modal */}
      {showAppraisalSettings && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(10, 14, 39, 0.95)", backdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ ...theme.glass, padding: "32px", borderRadius: "16px", width: "95%", maxWidth: "1000px", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px", borderBottom: "2px solid rgba(102, 126, 234, 0.3)", paddingBottom: "20px" }}>
              <h2 style={{ color: theme.text, margin: 0, fontSize: "28px", fontWeight: "700", display: "flex", alignItems: "center", gap: "12px" }}>
                <Settings size={28} color={colors.primary} />
                Appraisal Settings
              </h2>
              <button onClick={() => setShowAppraisalSettings(false)} style={{ background: "none", border: "none", color: theme.textSecondary, fontSize: "28px", cursor: "pointer", padding: "4px", borderRadius: "4px", transition: "all 0.2s" }} onMouseOver={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.1)"} onMouseOut={(e) => e.currentTarget.style.background = "none"}>×</button>
            </div>

            {/* Default Margin */}
            <div style={{ marginBottom: "32px" }}>
              <h3 style={{ color: colors.primary, fontSize: "15px", fontWeight: "700", marginBottom: "20px", display: "flex", alignItems: "center", gap: "8px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                <Percent size={18} color={colors.primary} />
                Default Appraisal Margin
              </h3>
              <div style={{ ...theme.glass, padding: "24px", borderRadius: "16px" }}>
                <label style={{ display: "block", color: theme.textSecondary, marginBottom: "12px", fontSize: "14px", fontWeight: "500" }}>
                  Default Percentage of Market Value
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <input 
                    type="number" 
                    min="50" 
                    max="120" 
                    value={appraisalSettings.defaultMargin} 
                    onChange={(e) => setAppraisalSettings({ ...appraisalSettings, defaultMargin: parseInt(e.target.value) })} 
                    style={{ width: "120px", padding: "12px 16px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px", color: theme.text, fontSize: "16px", fontWeight: "600", textAlign: "center" }} 
                  />
                  <span style={{ color: theme.text, fontSize: "16px", fontWeight: "600" }}>%</span>
                </div>
                <div style={{ fontSize: "13px", color: theme.textSecondary, padding: "12px", background: "rgba(59, 130, 246, 0.1)", borderRadius: "8px", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
                  <strong style={{ color: colors.primary }}>Example:</strong> If market value is $20,000, offer will be <strong style={{ color: theme.text }}>${Math.round(20000 * (appraisalSettings.defaultMargin / 100)).toLocaleString()}</strong>
                </div>
              </div>
            </div>

            {/* Make/Model Specific Margins */}
            <div style={{ marginBottom: "32px" }}>
              <h3 style={{ color: colors.primary, fontSize: "15px", fontWeight: "700", marginBottom: "20px", display: "flex", alignItems: "center", gap: "8px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                <Car size={18} color={colors.primary} />
                Make/Model Specific Margins
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "16px", marginBottom: "20px" }}>
                {Object.entries(appraisalSettings.makeModelMargins).map(([makeModel, margin]) => {
                  const [make, model] = makeModel.split('-');
                  return (
                    <div key={makeModel} style={{ ...theme.glass, padding: "20px", borderRadius: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: theme.text, fontWeight: "600", fontSize: "15px", marginBottom: "4px" }}>{make} {model}</div>
                        <div style={{ color: theme.textSecondary, fontSize: "12px" }}>Custom margin</div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <input 
                            type="number" 
                            min="50" 
                            max="120" 
                            value={margin} 
                            onChange={(e) => setAppraisalSettings({ 
                              ...appraisalSettings, 
                              makeModelMargins: { ...appraisalSettings.makeModelMargins, [makeModel]: parseInt(e.target.value) } 
                            })} 
                            style={{ width: "70px", padding: "10px 12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px", color: theme.text, textAlign: "center", fontSize: "14px", fontWeight: "600" }} 
                          />
                          <span style={{ color: theme.text, fontSize: "14px" }}>%</span>
                        </div>
                        <button 
                          onClick={() => {
                            const newMargins = { ...appraisalSettings.makeModelMargins };
                            delete newMargins[makeModel];
                            setAppraisalSettings({ ...appraisalSettings, makeModelMargins: newMargins });
                          }}
                          style={{ background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", border: "none", borderRadius: "10px", color: "white", padding: "10px 14px", cursor: "pointer", fontSize: "13px", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px", transition: "all 0.2s" }}
                          onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.05)"}
                          onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button 
                onClick={() => {
                  const make = prompt("Enter Make (e.g., Ford):");
                  const model = prompt("Enter Model (e.g., Camry):");
                  const margin = prompt("Enter Margin % (e.g., 88):");
                  if (make && model && margin) {
                    const makeModel = `${make}-${model}`.replace(/\s+/g, '');
                    setAppraisalSettings({ 
                      ...appraisalSettings, 
                      makeModelMargins: { ...appraisalSettings.makeModelMargins, [makeModel]: parseInt(margin) } 
                    });
                  }
                }}
                style={{ padding: "12px 24px", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", border: "none", borderRadius: "10px", color: "white", cursor: "pointer", fontSize: "14px", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px", transition: "all 0.2s" }}
                onMouseOver={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                onMouseOut={(e) => e.currentTarget.style.transform = "translateY(0)"}
              >
                <Plus size={18} />
                Add Make/Model
              </button>
            </div>

            {/* CARFAX Adjustments */}
            <div style={{ marginBottom: "32px" }}>
              <h3 style={{ color: colors.primary, fontSize: "15px", fontWeight: "700", marginBottom: "20px", display: "flex", alignItems: "center", gap: "8px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                <AlertTriangle size={18} color={colors.primary} />
                CARFAX History Adjustments
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div style={{ ...theme.glass, padding: "24px", borderRadius: "16px", border: "2px solid rgba(239, 68, 68, 0.3)" }}>
                  <div style={{ fontSize: "14px", color: "#ef4444", marginBottom: "16px", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" }}>
                    <AlertTriangle size={18} color="#ef4444" />
                    Branded Title Deduction
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
                    <input 
                      type="number" 
                      min="0" 
                      max="50" 
                      value={appraisalSettings.brandedTitleDeduction} 
                      onChange={(e) => setAppraisalSettings({ ...appraisalSettings, brandedTitleDeduction: parseInt(e.target.value) })} 
                      style={{ width: "100px", padding: "12px 16px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px", color: theme.text, fontSize: "16px", fontWeight: "600", textAlign: "center" }} 
                    />
                    <span style={{ color: theme.text, fontSize: "16px", fontWeight: "600" }}>% reduction</span>
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: "12px", cursor: "pointer", padding: "12px", background: "rgba(239, 68, 68, 0.1)", borderRadius: "10px", border: "1px solid rgba(239, 68, 68, 0.2)" }}>
                    <input 
                      type="checkbox" 
                      checked={appraisalSettings.avoidBrandedTitles} 
                      onChange={(e) => setAppraisalSettings({ ...appraisalSettings, avoidBrandedTitles: e.target.checked })} 
                      style={{ width: "18px", height: "18px", cursor: "pointer" }} 
                    />
                    <span style={{ color: theme.text, fontSize: "14px", fontWeight: "500" }}>Flag "DO NOT PURCHASE" on branded titles</span>
                  </label>
                </div>
                <div style={{ ...theme.glass, padding: "24px", borderRadius: "16px", border: "2px solid rgba(245, 158, 11, 0.3)" }}>
                  <div style={{ fontSize: "14px", color: "#f59e0b", marginBottom: "20px", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" }}>
                    <AlertCircle size={18} color="#f59e0b" />
                    Accident History Deductions
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", color: theme.textSecondary, marginBottom: "8px", fontSize: "13px", fontWeight: "500" }}>Minor Accident</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <input 
                        type="number" 
                        min="0" 
                        max="20" 
                        value={appraisalSettings.minorAccidentDeduction} 
                        onChange={(e) => setAppraisalSettings({ ...appraisalSettings, minorAccidentDeduction: parseInt(e.target.value) })} 
                        style={{ width: "90px", padding: "12px 16px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px", color: theme.text, fontSize: "14px", fontWeight: "600", textAlign: "center" }} 
                      />
                      <span style={{ color: theme.text, fontSize: "14px" }}>%</span>
                    </div>
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", color: theme.textSecondary, marginBottom: "8px", fontSize: "13px", fontWeight: "500" }}>Moderate Accident</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <input 
                        type="number" 
                        min="0" 
                        max="30" 
                        value={appraisalSettings.moderateAccidentDeduction} 
                        onChange={(e) => setAppraisalSettings({ ...appraisalSettings, moderateAccidentDeduction: parseInt(e.target.value) })} 
                        style={{ width: "90px", padding: "12px 16px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px", color: theme.text, fontSize: "14px", fontWeight: "600", textAlign: "center" }} 
                      />
                      <span style={{ color: theme.text, fontSize: "14px" }}>%</span>
                    </div>
                  </div>
                  <div>
                    <label style={{ display: "block", color: theme.textSecondary, marginBottom: "8px", fontSize: "13px", fontWeight: "500" }}>Major Accident</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <input 
                        type="number" 
                        min="0" 
                        max="40" 
                        value={appraisalSettings.majorAccidentDeduction} 
                        onChange={(e) => setAppraisalSettings({ ...appraisalSettings, majorAccidentDeduction: parseInt(e.target.value) })} 
                        style={{ width: "90px", padding: "12px 16px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px", color: theme.text, fontSize: "14px", fontWeight: "600", textAlign: "center" }} 
                      />
                      <span style={{ color: theme.text, fontSize: "14px" }}>%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Example Calculation */}
            <div style={{ ...theme.glass, padding: "24px", borderRadius: "16px", border: "1px solid rgba(59, 130, 246, 0.3)", marginBottom: "32px", background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)" }}>
              <h4 style={{ color: colors.primary, fontSize: "15px", fontWeight: "700", marginBottom: "20px", display: "flex", alignItems: "center", gap: "8px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                <Lightbulb size={18} color={colors.primary} />
                Example Calculation
              </h4>
              <div style={{ color: theme.text, fontSize: "14px", lineHeight: "2" }}>
                <div style={{ marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "12px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                  <span style={{ color: theme.textSecondary }}>Market Value:</span>
                  <strong style={{ fontSize: "16px", color: theme.text }}>$20,000</strong>
                </div>
                <div style={{ marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "12px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                  <span style={{ color: theme.textSecondary }}>Base Margin ({appraisalSettings.defaultMargin}%):</span>
                  <strong style={{ fontSize: "16px", color: colors.primary }}>${Math.round(20000 * (appraisalSettings.defaultMargin / 100)).toLocaleString()}</strong>
                </div>
                <div style={{ marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "12px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                  <span style={{ color: "#f59e0b" }}>- Minor Accident (-{appraisalSettings.minorAccidentDeduction}%):</span>
                  <strong style={{ fontSize: "16px", color: "#f59e0b" }}>-${Math.round(20000 * (appraisalSettings.minorAccidentDeduction / 100)).toLocaleString()}</strong>
                </div>
                <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "2px solid rgba(59, 130, 246, 0.3)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ color: theme.textSecondary, fontWeight: "600", fontSize: "15px" }}>Final Offer:</span>
                  <strong style={{ fontSize: "20px", color: colors.primary }}>${Math.round(20000 * ((appraisalSettings.defaultMargin - appraisalSettings.minorAccidentDeduction) / 100)).toLocaleString()}</strong>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", gap: "12px", marginTop: "32px", borderTop: "1px solid rgba(102, 126, 234, 0.2)", paddingTop: "20px" }}>
              <button onClick={() => setShowAppraisalSettings(false)} style={{ flex: 1, padding: "12px 24px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "10px", color: theme.text, cursor: "pointer", fontSize: "14px", fontWeight: "600", transition: "all 0.2s" }}>Close</button>
              <button onClick={() => { showToast("Appraisal settings saved!", "success"); setShowAppraisalSettings(false); }} style={{ flex: 1, padding: "12px 24px", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", border: "none", borderRadius: "10px", color: "white", cursor: "pointer", fontSize: "14px", fontWeight: "600", transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                <CheckCircle size={18} />
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      <ChatAssistant customers={customers} onAction={handleChatAssistantAction} />
      </div>

      {/* CSS for Animations */}
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
    );
  };

function App() {
  console.log('App component rendered');
  
  // Check for dealer authentication
  const [dealer, setDealer] = useState(() => {
    const saved = localStorage.getItem('dealer') || localStorage.getItem('vincentrix_dealer');
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState(() => {
    return localStorage.getItem('token') || localStorage.getItem('vincentrix_token') || null;
  });
  
  // Legacy user state for backward compatibility
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('vincentrix_user');
    return saved ? JSON.parse(saved) : null;
  });
  
  // Handle dealer login
  const handleDealerLogin = (dealerData, authToken) => {
    setDealer(dealerData);
    setToken(authToken);
    // Store in both key formats for compatibility
    localStorage.setItem('dealer', JSON.stringify(dealerData));
    localStorage.setItem('dealership', JSON.stringify(dealerData)); // New format
    localStorage.setItem('token', authToken);
    localStorage.setItem('vincentrix_dealer', JSON.stringify(dealerData));
    localStorage.setItem('vincentrix_token', authToken);
    
    // Show loading screen for at least 3 seconds after login
    setIsInitialLoad(true);
    hasInitialLoadCompletedRef.current = false;
    
    // Hide loading screen after 3 seconds
    setTimeout(() => {
      setIsInitialLoad(false);
      hasInitialLoadCompletedRef.current = true;
    }, 3000);
  };
  
  // Handle logout
  const handleLogout = () => {
    setDealer(null);
    setToken(null);
    setUser(null);
    localStorage.removeItem('dealer');
    localStorage.removeItem('token');
    localStorage.removeItem('vincentrix_dealer');
    localStorage.removeItem('vincentrix_token');
    localStorage.removeItem('vincentrix_user');
  };
  const [activeTab, setActiveTab] = useState(() => {
    const stored = localStorage.getItem('vincentrix_activeTab');
    if (!stored) return 'dashboard';
    if (stored === 'simulator') return 'dashboard';
    return stored;
  });
  const [tabLoading, setTabLoading] = useState(false);
  const [previousTab, setPreviousTab] = useState(null);
  
  // Heavy tabs that need loading transition
  const heavyTabs = ['analytics', 'conversations', 'all-customers'];
  
  // Custom tab change handler with loading transition
  const handleTabChange = (newTab) => {
    // TEMPORARILY DISABLE LOADING FOR CONVERSATIONS TAB FOR TESTING
    if (newTab === 'conversations') {
      // Skip loading for conversations to test if it's causing re-renders
      setActiveTab(newTab);
      setTabLoading(false);
      setPreviousTab(null);
      return;
    }
    
    const isHeavyTab = heavyTabs.includes(newTab);
    const isSwitchingToHeavy = isHeavyTab && newTab !== activeTab;
    
    if (isSwitchingToHeavy) {
      setPreviousTab(activeTab);
      setTabLoading(true);
      setActiveTab(newTab);
      
      // Hide loading overlay after 200ms
      setTimeout(() => {
        setTabLoading(false);
        setPreviousTab(null);
      }, 200);
    } else {
      // Fast tabs (Dashboard, Simulator) or switching away from heavy tabs - no loading
      setActiveTab(newTab);
      setTabLoading(false);
      setPreviousTab(null);
    }
  };
  
  // Persist activeTab to localStorage whenever it changes
  useEffect(() => {
    console.log('useEffect triggered - [activeTab]');
    localStorage.setItem('vincentrix_activeTab', activeTab);
  }, [activeTab]);
  
  // Use custom hook for customer management
  const {
    customers,
    setCustomers: originalSetCustomers,
    fetchCustomers,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    loading: customersLoading
  } = useCustomers(user);

  // Wrap setCustomers with logging
  const setCustomers = useCallback((value) => {
    console.log('setCustomers changed to:', Array.isArray(value) ? `${value.length} customers` : typeof value === 'function' ? 'function' : value);
    originalSetCustomers(value);
  }, [originalSetCustomers]);
  
  // Initial load state - must be declared before handleLogin
  const [isInitialLoad, setIsInitialLoad] = useState(false);
  const hasInitialLoadCompletedRef = useRef(false);
  
  const [selectedCustomer, _setSelectedCustomer] = useState(null);
  const [conversations, _setConversations] = useState({});
  const [newMessage, _setNewMessage] = useState('');
  const setNewMessage = useCallback((value) => {
    console.log('setNewMessage changed to:', value);
    _setNewMessage(value);
  }, []);
  const [aiResponding, _setAiResponding] = useState(false);
  const [aiPausedCustomers, setAiPausedCustomers] = useState({});
  const [customerTyping, setCustomerTyping] = useState(false);
  const [statusFilter, _setStatusFilter] = useState('all');
  const [searchQuery, _setSearchQuery] = useState('');

  // Wrap state setters with logging
  const setSelectedCustomer = useCallback((value) => {
    console.log('setSelectedCustomer changed to:', value?.id || value);
    _setSelectedCustomer(value);
  }, []);

  const setConversations = useCallback((value) => {
    console.log('setConversations changed to:', typeof value === 'function' ? 'function' : Object.keys(value).length + ' conversations');
    _setConversations(value);
  }, []);

  const setAiResponding = useCallback((value) => {
    console.log('setAiResponding changed to:', value);
    _setAiResponding(value);
  }, []);

  const setStatusFilter = useCallback((value) => {
    console.log('setStatusFilter changed to:', value);
    _setStatusFilter(value);
  }, []);

  const setSearchQuery = useCallback((value) => {
    console.log('setSearchQuery changed to:', value);
    _setSearchQuery(value);
  }, []);
  const [appraisalDateFilter, setAppraisalDateFilter] = useState('all');
  const [recentActions, setRecentActions] = useState([]);
  const [hotLeads, setHotLeads] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [tradeIns, setTradeIns] = useState([]);
  const messagesEndRef = useRef(null);
  const conversationsRef = useRef(conversations);
  useEffect(() => {
    if (customers && customers.length > 0) {
      const pausedMap = customers.reduce((acc, customer) => {
        acc[customer.id] = Boolean(customer.aiPaused);
        return acc;
      }, {});
      setAiPausedCustomers(pausedMap);
    } else {
      setAiPausedCustomers({});
    }
  }, [customers]);
  const [notifications, setNotifications] = useState([]);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const isTogglingThemeRef = useRef(false);
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [manualLeadForm, setManualLeadForm] = useState({ firstName: "", lastName: "", business: "", email: "", zip: "", cPhone: "", source: "facebook", notes: "", vin: "", vehicleYear: "", vehicleMake: "", vehicleModel: "", vehicleTrim: "", mileage: "", payoffAmount: "", perDiem: "", payoffGoodUntil: "", lenderName: "", payoffDate: "", assignedTo: "", priority: "normal", scheduleAppointment: false, appointmentDate: "", appointmentTime: "14:00", appointmentAppraiser: "Tyler Brodsky", appointmentNotes: "" });
  const [vinDecoding, setVinDecoding] = useState(false);
  const [vinData, setVinData] = useState(null);
  
  // Handle login callback for LoginPage
  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('vincentrix_user', JSON.stringify(userData));
    setIsInitialLoad(true);
  };

  // Track when initial load completes - only runs once after login
  useEffect(() => {
    console.log('useEffect triggered - [dealer, user, customersLoading, isInitialLoad]', { dealer: !!dealer, user: !!user, customersLoading, isInitialLoad });
    if ((dealer || user) && customersLoading === false && isInitialLoad && !hasInitialLoadCompletedRef.current) {
      // Small delay for smooth fade out
      const timeoutId = setTimeout(() => {
        setIsInitialLoad(false);
        hasInitialLoadCompletedRef.current = true; // Mark as completed, never show again
      }, TIMING.FADE_OUT_DELAY_MS);
      
      return () => clearTimeout(timeoutId);
    }
  }, [dealer, user, customersLoading, isInitialLoad]);

  // Toast notifications
  const { toasts, showToast, removeToast } = useToast();

  // Memoize theme toggle handler to prevent double execution
  const handleToggleTheme = useCallback(() => {
    // Prevent double execution (React.StrictMode in development)
    if (isTogglingThemeRef.current) {
      return;
    }
    
    isTogglingThemeRef.current = true;
    
    // Calculate new mode before updating state
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    
    // Show toast with the new mode value
    showToast(newMode ? 'Dark mode activated 🌙' : 'Light mode activated ☀️', 'info');
    
    // Reset guard after a short delay
    setTimeout(() => {
      isTogglingThemeRef.current = false;
    }, 100);
  }, [isDarkMode, showToast]);

  // CRM Modal States
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailCustomer, setDetailCustomer] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showTradeInModal, setShowTradeInModal] = useState(false);
  const [tradeInForm, setTradeInForm] = useState({ year: "", make: "", model: "", trim: "", mileage: "", vin: "" });
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulingCustomer, setSchedulingCustomer] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsTab, setSettingsTab] = useState('notifications');
  
  // Appraisal Margin Settings
  const [appraisalSettings, setAppraisalSettings] = useState({
    defaultMargin: 85, // Default 85% of market value
    makeModelMargins: {
      'Nissan-Altima': 88,
      'Honda-Accord': 87,
      'Ford-F150': 90,
      'Chevrolet-Silverado': 89,
      'Tesla-Model3': 82,
      'BMW-3Series': 75,
      'MercedesBenz-CClass': 73
    },
    brandedTitleDeduction: 30, // 30% reduction for branded titles
    minorAccidentDeduction: 8, // 8% reduction for minor accidents
    moderateAccidentDeduction: 15, // 15% reduction for moderate accidents
    majorAccidentDeduction: 25, // 25% reduction for major accidents
    avoidBrandedTitles: true // Flag to avoid or warn on branded titles
  });
  const [showAppraisalSettings, setShowAppraisalSettings] = useState(false);

  // CSV Import States
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [columnMapping, setColumnMapping] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleVIN: '',
    vehicleMileage: '',
    lastServiceDate: '',
    serviceType: ''
  });


  // Theme colors with glassmorphism
  // Memoize theme to prevent creating new object reference on every render
  const theme = useMemo(() => {
    return isDarkMode ? themeConstants.dark : themeConstants.light;
  }, [isDarkMode]);

  const [notifiedCustomers, setNotifiedCustomers] = useState(new Set());
  const [advancedFilters] = useState({
    minOffer: '',
    maxOffer: '',
    vehicleType: 'all',
    dateAdded: 'all'
  });
  const [dateRange, setDateRange] = useState('7days');
  const isFirstLoadRef = useRef(true);
  const prevCustomerStatusesRef = useRef({});

  useEffect(() => {
    console.log('useEffect triggered - [user]', { user: !!user });
    if (user) {
      localStorage.setItem('vincentrix_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('vincentrix_user');
    }
  }, [user]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  // scrollToBottom function removed - unused

  /**
   * Fetch recent actions data
   */
  const fetchRecentActions = useCallback(async () => {
    try {
      const data = await analyticsAPI.getRecentActions();
      setRecentActions(data.actions);
    } catch (error) {
      console.error('Error fetching recent actions:', error);
    }
  }, []);

  /**
   * Fetch hot leads data
   */
  const fetchHotLeads = useCallback(async () => {
    try {
      const data = await analyticsAPI.getHotLeads();
      setHotLeads(data.leads);
    } catch (error) {
      console.error('Error fetching hot leads:', error);
    }
  }, []);

  /**
   * Fetch appointments data
   */
  const fetchAppointments = useCallback(async () => {
    try {
      const data = await analyticsAPI.getAppointments();
      setAppointments(data.appointments);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  }, []);

  /**
   * Fetch trade-ins data
   */
  const fetchTradeIns = useCallback(async () => {
    try {
      const data = await analyticsAPI.getTradeIns();
      setTradeIns(data.tradeIns);
    } catch (error) {
      console.error('Error fetching trade-ins:', error);
    }
  }, []);

  // Memoize analytics calculations to avoid recalculating on every render
  const analytics = useMemo(() => {
    if (!customers || customers.length === 0) {
      return {
        totalConversations: 0,
        responseRate: 0,
        acquisitionRate: 0,
        avgOfferValue: 0,
        conversionFunnel: {
          contacted: 0,
          responded: 0,
          interested: 0,
          appointmentSet: 0,
          completed: 0
        }
      };
    }

    const totalConversations = customers.filter(c => 
      c.conversations && c.conversations.length > 0
    ).length;

    const customersReplied = customers.filter(c =>
      c.conversations && c.conversations.some(msg => msg.sender === 'customer')
    ).length;

    const responseRate = totalConversations > 0 
      ? parseFloat(((customersReplied / totalConversations) * 100).toFixed(1))
      : 0;

    const appointmentsSet = customers.filter(c => 
      c.acquisitionStatus === 'appointment-set'
    ).length;

    const acquisitionRate = totalConversations > 0
      ? parseFloat(((appointmentsSet / totalConversations) * 100).toFixed(1))
      : 0;

    const offersWithValue = customers.filter(c => c.currentOffer && c.currentOffer !== 'N/A');
    const totalOfferValue = offersWithValue.reduce((sum, c) => {
      const offerValue = parseFloat(String(c.currentOffer).replace(/[$,]/g, ''));
      return sum + (isNaN(offerValue) ? 0 : offerValue);
    }, 0);
    const avgOfferValue = offersWithValue.length > 0
      ? Math.round(totalOfferValue / offersWithValue.length)
      : 0;

    const conversionFunnel = {
      contacted: customers.length,
      responded: customersReplied,
      interested: customers.filter(c => 
        c.acquisitionStatus === 'hot-lead' || 
        c.acquisitionStatus === 'hot-opportunity'
      ).length,
      appointmentSet: appointmentsSet,
      completed: customers.filter(c => 
        c.acquisitionStatus === 'completed' || c.status === 'closed-won'
      ).length
    };

    return {
      totalConversations,
      responseRate,
      acquisitionRate,
      avgOfferValue,
      conversionFunnel
    };
  }, [customers]);

  useEffect(() => {
    console.log('useEffect triggered - [user, dateRange, fetchRecentActions, fetchHotLeads, fetchAppointments, fetchTradeIns]', { user: !!user, dateRange });
    if (user) {
      // Hook automatically fetches customers
      // Analytics are now calculated automatically via useMemo
      fetchRecentActions();
      fetchHotLeads();
      fetchAppointments();
      fetchTradeIns();
    }
  }, [user, dateRange, fetchRecentActions, fetchHotLeads, fetchAppointments, fetchTradeIns]);

  /**
   * Play notification sound
   */
  const playNotificationSound = () => {
    // Create a pleasant notification sound using Web Audio API
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Create a pleasant "ding" sound
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Could not play notification sound:', error);
    }
  };

  /**
   * Add notification to the list
   */
  const addNotification = useCallback((notification) => {
    setNotifications(prev => [notification, ...prev].slice(0, VALIDATION.MAX_NOTIFICATIONS));
    
    // Play notification sound
    if (notificationsEnabled) {
      playNotificationSound();
    }
  }, [notificationsEnabled]);
  
  // Sync hook's customers with notification logic
  useEffect(() => {
    console.log('useEffect triggered - [customers, notificationsEnabled]', { customersCount: customers.length, notificationsEnabled });
    if (customers.length > 0) {
      // On first load, just set up the initial state
      if (isFirstLoadRef.current) {
        const newStatuses = {};
        customers.forEach(c => {
          newStatuses[c.id] = c.status;
        });
        prevCustomerStatusesRef.current = newStatuses;
        isFirstLoadRef.current = false;
        return;
      }
      
      // After first load, check for status changes
      if (notificationsEnabled) {
        customers.forEach(customer => {
          const prevStatus = prevCustomerStatusesRef.current[customer.id];
          const currentStatus = customer.status;
          const notificationKey = `hot-lead-${customer.id}`;
          
          // Only notify when status changes FROM non-hot-lead TO hot-lead
          if (prevStatus && prevStatus !== 'hot-lead' && currentStatus === 'hot-lead') {
            // Use functional update to avoid dependency on notifiedCustomers
            setNotifiedCustomers(prev => {
              if (!prev.has(notificationKey)) {
                addNotification({
                  id: Date.now() + Math.random(),
                  customerId: customer.id,
                  type: 'hot-lead',
                  message: `🔥 ${customer.name} is now a hot acquisition opportunity! Offer: ${customer.currentOffer}`,
                  timestamp: new Date().toISOString()
                });
                
                const newSet = new Set(prev);
                newSet.add(notificationKey);
                return newSet;
              }
              return prev;
            });
          }
          
          // Reset notification tracking if status changes away from hot-lead
          if (prevStatus === 'hot-lead' && currentStatus !== 'hot-lead') {
            setNotifiedCustomers(prev => {
              const newSet = new Set(prev);
              newSet.delete(notificationKey);
              return newSet;
            });
          }
        });
      }
      
      // Update statuses tracking for next comparison
      const newStatuses = {};
      customers.forEach(c => {
        newStatuses[c.id] = c.status;
      });
      prevCustomerStatusesRef.current = newStatuses;
    }
  }, [customers, notificationsEnabled, addNotification]);

  /**
   * Fetch conversation for a customer
   * @param {string} customerId - Customer ID
   * @param {AbortSignal} signal - Optional AbortSignal for request cancellation
   */
  const fetchConversation = useCallback(async (customerId, signal) => {
    try {
      const data = await customerAPI.getConversations(customerId, signal);
      
      if (signal?.aborted) return;
      
      if (data.success) {
        // Transform messages from backend format to frontend format
        // Backend uses: { role: 'customer'|'assistant', content: 'text', timestamp: '...' }
        // Frontend expects: { sender: 'customer'|'assistant', text: 'text' or content: 'text', timestamp: '...' }
        const transformedMessages = (data.conversations || []).map(msg => {
          const rawSender = msg.sender || msg.role;
          let sender = 'ai';

          if (rawSender) {
            if (rawSender === 'assistant' || rawSender === 'ai') {
              sender = 'ai';
            } else if (rawSender === 'user' || rawSender === 'customer') {
              sender = 'customer';
            } else {
              sender = rawSender;
            }
          }

          const normalizedContent = msg.message || msg.text || msg.content || '';

          return {
            ...msg,
            sender,
            text: normalizedContent,
            content: normalizedContent,
            message: normalizedContent,
            timestamp: msg.timestamp,
            role: sender === 'ai' ? 'assistant' : sender
          };
        });
        
        if (signal?.aborted) return;
        
        setConversations(prev => {
          const previousMessages = prev[customerId] || [];
          const previousMessageCount = previousMessages.length;
          const newMessageCount = transformedMessages.length;
          
          // Check if new AI message arrived (message count increased and last message is from assistant)
          const hasNewAIMessage = newMessageCount > previousMessageCount && 
            (transformedMessages[newMessageCount - 1]?.sender === 'assistant' ||
             transformedMessages[newMessageCount - 1]?.sender === 'ai');
          
          // Hide typing indicator when AI response arrives
          if (hasNewAIMessage) {
            setAiResponding(false);
          }
          
          return {
            ...prev,
            [customerId]: transformedMessages
          };
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return; // Request was aborted, ignore
      }
      // Hide typing indicator on error
      setAiResponding(false);
      setCustomerTyping(false);
      // Silently handle errors - they're already handled by the API layer
    }
  }, []);

  useEffect(() => {
    if (!selectedCustomer?.id || activeTab !== 'conversations') {
      setCustomerTyping(false);
      return;
    }

    const API_URL = process.env.REACT_APP_API_URL || API_CONFIG.DEFAULT_URL;
    let isActive = true;
    let previousMessageCount = (conversationsRef.current[selectedCustomer.id] || []).length || 0;

    let typingTimeoutId = null;

    const fetchLatestConversations = async () => {
      try {
        const authToken = localStorage.getItem('token') || localStorage.getItem('vincentrix_token') || null;
        const response = await fetch(`${API_URL}/api/customers/${selectedCustomer.id}/conversations`, {
          headers: {
            'Content-Type': 'application/json',
            ...(authToken && { 'Authorization': `Bearer ${authToken}` })
          }
        });
        if (!isActive || !response.ok) {
          return;
        }

        const data = await response.json();

        if (!isActive) {
          return;
        }

        if (data.success && data.conversations) {
          const newMessageCount = data.conversations.length;

          if (newMessageCount > previousMessageCount) {
            fetchCustomers()
              .then(syncSelectedCustomer)
              .catch(err => console.error('Customer refresh error:', err));
            const lastMessage = data.conversations[newMessageCount - 1];

            if (lastMessage?.sender === 'customer') {
              setCustomerTyping(true);

              typingTimeoutId = setTimeout(() => {
                if (!isActive) return;
                setCustomerTyping(false);
                setConversations(prev => ({
                  ...prev,
                  [selectedCustomer.id]: data.conversations
                }));
                previousMessageCount = newMessageCount;
              }, 1500);
            } else {
              setConversations(prev => ({
                ...prev,
                [selectedCustomer.id]: data.conversations
              }));
              previousMessageCount = newMessageCount;
            }
          }
        }
      } catch (error) {
        if (!isActive) return;
        console.error('Error polling conversations:', error);
      }
    };

    fetchLatestConversations();

    const pollInterval = setInterval(fetchLatestConversations, 2000);

    return () => {
      isActive = false;
      clearInterval(pollInterval);
      if (typingTimeoutId) {
        clearTimeout(typingTimeoutId);
      }
      setCustomerTyping(false);
    };
  }, [
    selectedCustomer?.id,
    activeTab,
    fetchCustomers
  ]);

  // Memoize filtered customers to avoid recalculating on every render
  const filteredCustomers = useMemo(() => {
    return applyCustomerFilters(customers, { statusFilter, searchQuery });
  }, [customers, statusFilter, searchQuery]);

  useEffect(() => {
    if (!selectedCustomer || customers.length === 0) {
      return;
    }

    const freshCustomer = customers.find(c => c.id === selectedCustomer.id);
    if (!freshCustomer) {
      return;
    }

    const statusChanged = freshCustomer.acquisitionStatus !== selectedCustomer.acquisitionStatus;
    const priorityChanged = freshCustomer.priority !== selectedCustomer.priority;
    const aiPausedChanged = freshCustomer.aiPaused !== selectedCustomer.aiPaused;

    if (statusChanged || priorityChanged || aiPausedChanged) {
      console.log(`Updating selectedCustomer: ${freshCustomer.acquisitionStatus}`);
      setSelectedCustomer(freshCustomer);
    }
  }, [customers]);

  // Auto-refresh Conversations tab to show new messages from Simulator
  // TEMPORARILY COMMENTED OUT FOR TESTING
  /*
  useEffect(() => {
    if (activeTab === 'conversations') {
      // Refresh immediately when tab opens
      fetchCustomers();
      
      // Then poll every 30 seconds while on Conversations tab (reduced from 3s for better performance)
      const interval = setInterval(() => {
        fetchCustomers();
      }, TIMING.CUSTOMER_REFRESH_INTERVAL_MS);
      
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // Only depend on activeTab to prevent re-triggering on every render
  */

  const handleDecodeVIN = async (vin) => {
    if (!vin || vin.length !== VALIDATION.VIN_LENGTH) {
      showToast(`VIN must be ${VALIDATION.VIN_LENGTH} characters`, 'error');
      return;
    }
    setVinDecoding(true);
    try {
      const vehicleData = await decodeVIN(vin);
      setVinData(vehicleData);
      setManualLeadForm({
        ...manualLeadForm,
        vehicleYear: vehicleData.year,
        vehicleMake: vehicleData.make,
        vehicleModel: vehicleData.model,
        vehicleTrim: vehicleData.trim,
        vin: vin
      });
      showToast('✅ VIN decoded successfully', 'success');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setVinDecoding(false);
    }
  };


  /**
   * Submit manual lead entry form
   */
  const handleManualLeadSubmit = async () => {
    // Validate required fields
    const missingFields = [];
    if (!manualLeadForm.firstName || manualLeadForm.firstName.trim() === '') {
      missingFields.push('First Name');
    }
    if (!manualLeadForm.lastName || manualLeadForm.lastName.trim() === '') {
      missingFields.push('Last Name');
    }
    if (!manualLeadForm.cPhone || manualLeadForm.cPhone.trim() === '') {
      missingFields.push('Cell Phone');
    }

    if (missingFields.length > 0) {
      const fieldList = missingFields.join(', ');
      showToast(`Please fill in required fields: ${fieldList}`, "error");
      return;
    }

    try {
      const assignedToValue = manualLeadForm.assignedTo || aiAssignLead();
      
      // Prepare request body with all fields
      const requestBody = {
        firstName: manualLeadForm.firstName.trim(),
        lastName: manualLeadForm.lastName.trim(),
        phone: manualLeadForm.cPhone.trim(),
        cPhone: manualLeadForm.cPhone.trim(), // Include both for compatibility
        email: manualLeadForm.email?.trim() || '',
        business: manualLeadForm.business?.trim() || '',
        zip: manualLeadForm.zip?.trim() || '',
        source: manualLeadForm.source || 'facebook',
        notes: manualLeadForm.notes?.trim() || '',
        vin: manualLeadForm.vin?.trim() || '',
        vehicleYear: manualLeadForm.vehicleYear?.trim() || '',
        vehicleMake: manualLeadForm.vehicleMake?.trim() || '',
        vehicleModel: manualLeadForm.vehicleModel?.trim() || '',
        vehicleTrim: manualLeadForm.vehicleTrim?.trim() || '',
        mileage: manualLeadForm.mileage?.trim() || '',
        payoffAmount: manualLeadForm.payoffAmount?.trim() || '',
        perDiem: manualLeadForm.perDiem?.trim() || '',
        payoffGoodUntil: manualLeadForm.payoffGoodUntil || '',
        lenderName: manualLeadForm.lenderName?.trim() || '',
        payoffDate: manualLeadForm.payoffDate || '',
        assignedTo: assignedToValue,
        priority: manualLeadForm.priority || 'normal',
        status: "in-conversation",
        manualEntry: true
      };


      const customer = await addCustomer(requestBody);

      const assignedName = customer?.assignedTo || requestBody.assignedTo || assignedToValue;
      
      // If appointment scheduling is enabled, schedule it
      if (manualLeadForm.scheduleAppointment && manualLeadForm.appointmentDate && manualLeadForm.appointmentTime && manualLeadForm.appointmentAppraiser) {
        try {
          // Format date to YYYY-MM-DD
          let formattedDate = String(manualLeadForm.appointmentDate).trim();
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!dateRegex.test(formattedDate)) {
            const dateObj = new Date(formattedDate);
            if (!isNaN(dateObj.getTime())) {
              const year = dateObj.getFullYear();
              const month = String(dateObj.getMonth() + 1).padStart(2, '0');
              const day = String(dateObj.getDate()).padStart(2, '0');
              formattedDate = `${year}-${month}-${day}`;
            }
          }

          // Format time to HH:MM
          let formattedTime = String(manualLeadForm.appointmentTime).trim().replace(/\s+/g, '');
          const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
          if (!timeRegex.test(formattedTime)) {
            throw new Error('Invalid time format');
          }

          const API_URL = process.env.REACT_APP_API_URL || API_CONFIG.DEFAULT_URL;
          const authToken = localStorage.getItem('token') || localStorage.getItem('vincentrix_token') || null;
          const scheduleResponse = await fetch(`${API_URL}/api/customers/${customer.id}/schedule-appointment`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(authToken && { 'Authorization': `Bearer ${authToken}` })
            },
            body: JSON.stringify({
              date: formattedDate,
              time: formattedTime,
              appraiser: manualLeadForm.appointmentAppraiser,
              notes: manualLeadForm.appointmentNotes || ''
            })
          });

          const scheduleData = await scheduleResponse.json();
          if (scheduleResponse.ok && scheduleData.success) {
            showToast(`✅ Customer added and appointment scheduled!`, "success");
          } else {
            showToast(`✅ Customer added, but appointment scheduling failed: ${scheduleData.message || 'Unknown error'}`, "error");
          }
        } catch (scheduleError) {
          console.error('Error scheduling appointment:', scheduleError);
          showToast(`✅ Customer added, but appointment scheduling failed: ${scheduleError.message}`, "error");
        }
      } else {
        showToast(`✅ Acquisition opportunity added and assigned to ${assignedName}`, "success");
      }

      setShowManualEntryModal(false);
      setManualLeadForm({ firstName: "", lastName: "", business: "", email: "", zip: "", cPhone: "", source: "facebook", notes: "", vin: "", vehicleYear: "", vehicleMake: "", vehicleModel: "", vehicleTrim: "", mileage: "", payoffAmount: "", perDiem: "", payoffGoodUntil: "", lenderName: "", payoffDate: "", assignedTo: "", priority: "normal", scheduleAppointment: false, appointmentDate: "", appointmentTime: "14:00", appointmentAppraiser: "Tyler Brodsky", appointmentNotes: "" });
      setVinData(null);
      // Customer added via hook, state already updated
    } catch (error) {
      console.error('Error submitting customer:', error.message || error);

      let errorMessage = 'Failed to add lead: ';
      
      if (error.message) {
        errorMessage += error.message;
      } else if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage += 'Network error: Could not connect to server. Please check if the backend is running on http://localhost:3001 and CORS is enabled.';
      } else if (error instanceof SyntaxError) {
        errorMessage += 'Invalid response from server: ' + error.message;
      } else {
        errorMessage += 'An unexpected error occurred. Please check the console for details.';
      }

      showToast(errorMessage, "error");
    }
  };

  /**
   * View customer detail modal
   * @param {Object} customer - Customer object
   */
  const handleViewCustomerDetail = async (customer) => {
    try {
      const data = await customerAPI.getById(customer.id);
      setDetailCustomer(data.customer);
      setShowDetailModal(true);
    } catch (error) {
      showToast("Error loading customer details", "error");
    }
  };

  /**
   * Open edit customer modal
   * @param {Object} customer - Customer object to edit
   */
  const handleEditCustomer = (customer) => {
    setEditingCustomer({
      ...customer,
      firstName: customer.firstName || customer.name?.split(" ")[0] || "",
      lastName: customer.lastName || customer.name?.split(" ").slice(1).join(" ") || "",
      cellPhone: customer.cellPhone || customer.phone || customer.cPhone || "",
      address: customer.address || "",
      city: customer.city || "",
      state: customer.state || "",
      zipCode: customer.zipCode || "",
      homePhone: customer.homePhone || "",
      workPhone: customer.workPhone || "",
      email: customer.email || "",
      birthday: customer.birthday || "",
      preferredLanguage: customer.preferredLanguage || "",
      vehicleType: customer.vehicleType || "new",
      vehicleYear: customer.vehicleYear || "",
      vehicleMake: customer.vehicleMake || "",
      vehicleModel: customer.vehicleModel || "",
      vehicleTrim: customer.vehicleTrim || "",
      stockNumber: customer.stockNumber || "",
      salesperson: customer.salesperson || "",
      salesManager: customer.salesManager || "",
      salesStatus: customer.salesStatus || "New",
      source: customer.source || "facebook"
    });
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  /**
   * Submit customer edit form
   */
  const handleEditSubmit = async () => {
    if (!editingCustomer.firstName || !editingCustomer.lastName || !editingCustomer.cellPhone) {
      showToast("Please fill in First Name, Last Name, and Cell Phone", "error");
      return;
    }
    try {
      await updateCustomer(editingCustomer.id, editingCustomer);
      showToast("Customer updated successfully!", "success");
      setShowEditModal(false);
      setEditingCustomer(null);
      // Customer state automatically updated by hook
    } catch (error) {
      showToast("Error: " + error.message, "error");
    }
  };

  /**
   * Delete a customer
   * @param {string} customerId - Customer ID to delete
   */
  const handleScheduleAppointment = (customer) => {
    setSchedulingCustomer(customer);
    setShowScheduleModal(true);
  };

  const handleAppointmentScheduled = (updatedCustomer) => {
    // Refresh customer list
    fetchCustomers();
    showToast("Appointment scheduled successfully!", "success");
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await deleteCustomer(customerId);
      showToast("Customer deleted!", "success");
      setShowDetailModal(false);
    } catch (error) {
      showToast("Error: " + error.message, "error");
    }
  };

  const handleClearAllCustomers = async () => {
    if (!window.confirm('Are you sure you want to delete ALL customers? This cannot be undone!')) {
      return;
    }
    try {
      await customerAPI.clearAll();
      showToast('All customers cleared!', 'success');
      setShowDetailModal(false);
      setDetailCustomer(null);
      setNotifiedCustomers(new Set());
      await fetchCustomers();
    } catch (error) {
      showToast('Error: ' + error.message, 'error');
    }
  };

  /**
   * Open trade-in modal for a customer
   * @param {Object} customer - Customer object
   */
  const handleAddTradeIn = (customer) => {
    setDetailCustomer(customer);
    setTradeInForm({ year: "", make: "", model: "", trim: "", mileage: "", vin: "" });
    setShowTradeInModal(true);
  };

  /**
   * Submit trade-in form
   */
  const handleTradeInSubmit = async () => {
    if (!tradeInForm.year || !tradeInForm.make || !tradeInForm.model) {
      showToast("Please fill in Year, Make, and Model", "error");
      return;
    }
    try {
      await customerAPI.addTradeIn(detailCustomer.id, tradeInForm);
      showToast("Trade-in added!", "success");
      setShowTradeInModal(false);
      fetchCustomers();
      if (showDetailModal) handleViewCustomerDetail({ id: detailCustomer.id });
    } catch (error) {
      showToast("Error: " + error.message, "error");
    }
  };

  /**
   * Delete a trade-in from a customer
   * @param {string} customerId - Customer ID
   * @param {number} tradeInIndex - Trade-in index to delete
   */
  const handleDeleteTradeIn = async (customerId, tradeInIndex) => {
    if (!window.confirm("Delete this trade-in?")) return;
    try {
      await customerAPI.deleteTradeIn(customerId, tradeInIndex);
      showToast("Trade-in deleted!", "success");
      handleViewCustomerDetail({ id: customerId });
    } catch (error) {
      showToast("Error: " + error.message, "error");
    }
  };

  /**
   * Handle CSV file upload and parse it
   * @param {File} file - CSV file to upload
   */
  const handleFileUpload = (file) => {
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      showToast('Please upload a CSV file', 'error');
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          showToast('Error parsing CSV file', 'error');
          console.error('CSV Parse Errors:', results.errors);
          return;
        }

        setImportFile(file);
        setImportPreview(results.data);
        
        // Auto-detect column mappings
        if (results.data.length > 0) {
          const headers = Object.keys(results.data[0]);
          const autoMapping = { ...columnMapping };
          
          headers.forEach(header => {
            const lower = header.toLowerCase().replace(/[^a-z]/g, '');
            
            if (lower.includes('firstname') || lower === 'first') autoMapping.firstName = header;
            else if (lower.includes('lastname') || lower === 'last') autoMapping.lastName = header;
            else if (lower.includes('email')) autoMapping.email = header;
            else if (lower.includes('phone') || lower.includes('mobile') || lower.includes('cell')) autoMapping.phone = header;
            else if (lower.includes('address') && !lower.includes('email')) autoMapping.address = header;
            else if (lower.includes('city')) autoMapping.city = header;
            else if (lower.includes('state')) autoMapping.state = header;
            else if (lower.includes('zip')) autoMapping.zip = header;
            else if (lower.includes('year') && (lower.includes('vehicle') || lower.includes('car'))) autoMapping.vehicleYear = header;
            else if (lower.includes('make')) autoMapping.vehicleMake = header;
            else if (lower.includes('model') && !lower.includes('year')) autoMapping.vehicleModel = header;
            else if (lower.includes('vin')) autoMapping.vehicleVIN = header;
            else if (lower.includes('mileage') || lower.includes('miles') || lower.includes('odometer')) autoMapping.vehicleMileage = header;
            else if (lower.includes('lastservice') || lower.includes('servicedate')) autoMapping.lastServiceDate = header;
            else if (lower.includes('servicetype') || lower.includes('service')) autoMapping.serviceType = header;
          });
          
          setColumnMapping(autoMapping);
        }

        showToast(`✅ Loaded ${results.data.length} rows from ${file.name}`, 'success');
      },
      error: (error) => {
        showToast('Error reading file: ' + error.message, 'error');
      }
    });
  };

  /**
   * Import customers from CSV preview
   */
  const handleBulkImport = async () => {
    if (!columnMapping.firstName || !columnMapping.lastName) {
      showToast('Please map First Name and Last Name columns', 'error');
      return;
    }

    setIsImporting(true);
    
    try {
      // Map CSV data to customer objects
      const customers = importPreview.map(row => ({
        firstName: row[columnMapping.firstName] || '',
        lastName: row[columnMapping.lastName] || '',
        email: row[columnMapping.email] || '',
        phone: row[columnMapping.phone] || '',
        address: row[columnMapping.address] || '',
        city: row[columnMapping.city] || '',
        state: row[columnMapping.state] || '',
        zipCode: row[columnMapping.zip] || '',
        vehicleYear: row[columnMapping.vehicleYear] || '',
        vehicleMake: row[columnMapping.vehicleMake] || '',
        vehicleModel: row[columnMapping.vehicleModel] || '',
        vin: row[columnMapping.vehicleVIN] || '',
        mileage: row[columnMapping.vehicleMileage] || '',
        source: 'csv-import',
        status: 'new',
        manualEntry: true
      })).filter(c => c.firstName && c.lastName); // Only import rows with names

      if (customers.length === 0) {
        showToast('No valid customer data found to import', 'error');
        setIsImporting(false);
        return;
      }

      const API_URL = process.env.REACT_APP_API_URL || API_CONFIG.DEFAULT_URL;
      const authToken = localStorage.getItem('token') || localStorage.getItem('vincentrix_token') || null;

      const response = await fetch(`${API_URL}/api/customers/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        },
        body: JSON.stringify({ customers })
      });

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error(`Server returned non-JSON response (${response.status}): ${text.substring(0, 200)}`);
      }

      if (!response.ok) {
        const errorMessage = data.message || data.error || `Server error: ${response.status} ${response.statusText}`;
        console.error('Server error response:', {
          status: response.status,
          statusText: response.statusText,
          data: data
        });
        throw new Error(errorMessage);
      }

      const successCount = data.imported || data.customers?.length || customers.length;
      const errorCount = data.errors || 0;

      // Refresh customer list
      await fetchCustomers();

      // Show success message with detailed results
      if (errorCount > 0) {
        showToast(`⚠️ Import completed with errors: ${successCount} customers imported, ${errorCount} errors`, 'warning');
      } else {
        showToast(`✅ Successfully imported ${successCount} customer${successCount !== 1 ? 's' : ''}!`, 'success');
      }
      
      // Reset import state
      setShowImportModal(false);
      setImportFile(null);
      setImportPreview([]);
      setColumnMapping({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        vehicleYear: '',
        vehicleMake: '',
        vehicleModel: '',
        vehicleVIN: '',
        vehicleMileage: '',
        lastServiceDate: '',
        serviceType: ''
      });
      
    } catch (error) {
      console.error('Import error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        error: error
      });

      let errorMessage = 'Import failed: ';
      
      if (error.message) {
        errorMessage += error.message;
      } else if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage += 'Network error: Could not connect to server. Please check if the backend is running on ' + (process.env.REACT_APP_API_URL || 'http://localhost:3001');
      } else if (error instanceof SyntaxError) {
        errorMessage += 'Invalid response from server: ' + error.message;
      } else {
        errorMessage += 'An unexpected error occurred. Check browser console for details.';
      }
      
      showToast(errorMessage, 'error');
    } finally {
      setIsImporting(false);
    }
  };





  /**
   * Export customers to CSV
   */
  const handleExportCustomers = async () => {
    try {
      const blob = await analyticsAPI.exportCustomers();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vincentrix-customers-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      addNotification({
        id: Date.now(),
        type: 'export',
        message: '✅ Customer data exported successfully!',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error exporting customers:', error);
    }
  };


  // Memoize onChange handler to prevent unnecessary re-renders
  const handleMessageChange = useCallback((e) => {
    const value = e.target.value;
    setNewMessage(value);
  }, []); // Empty deps - setNewMessage is stable from useState

  /**
   * Send a message in conversation
   * Memoized to prevent unnecessary re-renders of MessageInput
   */
  const handleSendMessage = useCallback(async () => {
    if (!newMessage.trim() || !selectedCustomer) return;

    const messageText = newMessage.trim();
    const API_URL = process.env.REACT_APP_API_URL || API_CONFIG.DEFAULT_URL;
    const timestamp = new Date().toISOString();

    try {
      const dealership = localStorage.getItem('dealership') || localStorage.getItem('dealer');
      const dealershipId = dealership ? JSON.parse(dealership)?.id : 'champion-nissan';
      const authToken = localStorage.getItem('token') || localStorage.getItem('vincentrix_token') || null;
      const response = await fetch(`${API_URL}/api/customers/${selectedCustomer.id}/manual-message`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        },
        body: JSON.stringify({
          message: messageText,
          userId: user?.name || 'Tyler Brodsky',
          dealershipId: dealershipId
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to send message');
      }

      setConversations(prev => ({
        ...prev,
        [selectedCustomer.id]: [
          ...(prev[selectedCustomer.id] || []),
          {
            sender: 'user',
            message: messageText,
            text: messageText,
            timestamp
          }
        ]
      }));

      setAiPausedCustomers(prev => ({
        ...prev,
        [selectedCustomer.id]: true
      }));

      setCustomers(prev =>
        prev.map(c =>
          c.id === selectedCustomer.id
            ? { ...c, aiPaused: true, manualControl: true, assignedUser: user?.name || 'Tyler Brodsky' }
            : c
        )
      );

      setSelectedCustomer(prev => prev ? { ...prev, aiPaused: true, manualControl: true, assignedUser: user?.name || 'Tyler Brodsky' } : prev);

      setNewMessage('');
      setAiResponding(false);
      showToast('✅ Message sent (AI auto-paused)', 'success');

      fetchCustomers();
    } catch (error) {
      console.error('Send message error:', error);
      showToast(error.message || 'Failed to send message', 'error');
    }
  }, [newMessage, selectedCustomer, user, showToast, setCustomers, fetchCustomers]);

  const handleToggleAI = useCallback(async (customerId, paused) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || API_CONFIG.DEFAULT_URL;
      const authToken = localStorage.getItem('token') || localStorage.getItem('vincentrix_token') || null;
      const response = await fetch(`${API_URL}/api/customers/${customerId}/toggle-ai`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        },
        body: JSON.stringify({
          paused,
          userId: user?.name || 'Tyler Brodsky'
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to toggle AI');
      }

      setAiPausedCustomers(prev => ({
        ...prev,
        [customerId]: paused
      }));

      setCustomers(prev =>
        prev.map(c =>
          c.id === customerId
            ? { ...c, aiPaused: paused, manualControl: paused }
            : c
        )
      );

      if (selectedCustomer?.id === customerId) {
        setSelectedCustomer(prev =>
          prev ? { ...prev, aiPaused: paused, manualControl: paused } : prev
        );
      }

      showToast(paused ? '⏸️ AI Paused - Manual control enabled' : '▶️ AI Resumed', 'success');
    } catch (error) {
      console.error('Toggle AI error:', error);
      showToast('Failed to toggle AI', 'error');
    }
  }, [user, showToast, setCustomers, selectedCustomer, setSelectedCustomer]);

  // Memoize conversations tab display style to prevent remounts
  const conversationsTabDisplayStyle = useMemo(() => ({
    display: activeTab === 'conversations' ? 'block' : 'none'
  }), [activeTab]);

  useEffect(() => {
    if (!user) {
      return;
    }

    let isPolling = false;
    const pollCustomers = async () => {
      if (isPolling) {
        return;
      }
      isPolling = true;
      try {
        const latestCustomers = await fetchCustomers();
        syncSelectedCustomer(latestCustomers);
      } catch (error) {
        console.error('Customer polling error:', error);
      } finally {
        isPolling = false;
      }
    };

    // Initial poll to capture fresh statuses
    pollCustomers();

    const intervalId = setInterval(pollCustomers, TIMING.CUSTOMER_REFRESH_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [
    user,
    fetchCustomers
  ]);

  useEffect(() => {
    if (activeTab !== 'conversations' || !user) {
      return;
    }

    const pollInterval = setInterval(() => {
      console.log('Polling for customer updates...');
      fetchCustomers()
        .then(syncSelectedCustomer)
        .catch(err => console.error('Conversation tab customer refresh error:', err));
    }, 3000);

    return () => {
      console.log('Stopping customer polling');
      clearInterval(pollInterval);
    };
  }, [
    activeTab,
    user,
    fetchCustomers
  ]);

  const syncSelectedCustomer = useCallback((customerList) => {
    if (!selectedCustomer || !Array.isArray(customerList)) {
      return;
    }

    const updated = customerList.find(c => c.id === selectedCustomer.id);
    if (!updated) {
      return;
    }

    const hasChanges =
      updated.acquisitionStatus !== selectedCustomer.acquisitionStatus ||
      updated.status !== selectedCustomer.status ||
      updated.priority !== selectedCustomer.priority ||
      updated.aiPaused !== selectedCustomer.aiPaused;

    if (hasChanges) {
      console.log('Updating selectedCustomer with fresh data');
      setSelectedCustomer(updated);
    }
  }, [selectedCustomer, setSelectedCustomer]);

  // Helper to capitalize first letter of a string
  const capitalizeFirstLetter = (string) => {
    if (!string || string.length === 0) return string;
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  return (
    <>
    <Routes>
      <Route path="/" element={<Homepage />} />
      <Route path="/login" element={<LoginPage onLogin={handleDealerLogin} />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/sms-consent" element={<SMSConsent />} />
        <Route path="/sms-opt-in" element={<SmsOptIn />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute user={dealer || user}>
              <Dashboard
                settingsTab={settingsTab}
                setSettingsTab={setSettingsTab}
                dealer={dealer}
                handleLogout={handleLogout}
          isDarkMode={isDarkMode}
          theme={theme}
          colors={colors}
                isInitialLoad={isInitialLoad}
                user={user}
                hasInitialLoadCompletedRef={hasInitialLoadCompletedRef}
              notifications={notifications}
              setNotifications={setNotifications}
              handleToggleTheme={handleToggleTheme}
              setUser={setUser}
              setShowSettingsModal={setShowSettingsModal}
              setShowImportModal={setShowImportModal}
              setShowAppraisalSettings={setShowAppraisalSettings}
        notificationsEnabled={notificationsEnabled}
              setNotificationsEnabled={setNotificationsEnabled}
              addNotification={addNotification}
        activeTab={activeTab}
              handleTabChange={handleTabChange}
              dateRange={dateRange}
              setDateRange={setDateRange}
              analytics={analytics}
            customers={customers}
              recentActions={recentActions}
              tabLoading={tabLoading}
              previousTab={previousTab}
              conversationsTabDisplayStyle={conversationsTabDisplayStyle}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
            conversations={conversations}
            newMessage={newMessage}
            handleMessageChange={handleMessageChange}
            handleSendMessage={handleSendMessage}
            aiResponding={aiResponding}
            customerTyping={customerTyping}
            aiPausedCustomers={aiPausedCustomers}
            handleToggleAI={handleToggleAI}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            fetchConversation={fetchConversation}
            messagesEndRef={messagesEndRef}
              filteredCustomers={filteredCustomers}
              customersLoading={customersLoading}
              showToast={showToast}
          toasts={toasts} 
              removeToast={removeToast}
              showSettingsModal={showSettingsModal}
              showImportModal={showImportModal}
              showAppraisalSettings={showAppraisalSettings}
              appraisalSettings={appraisalSettings}
              setAppraisalSettings={setAppraisalSettings}
              appraisalDateFilter={appraisalDateFilter}
              setAppraisalDateFilter={setAppraisalDateFilter}
              showManualEntryModal={showManualEntryModal}
              setShowManualEntryModal={setShowManualEntryModal}
              manualLeadForm={manualLeadForm}
              setManualLeadForm={setManualLeadForm}
              handleManualLeadSubmit={handleManualLeadSubmit}
          vinDecoding={vinDecoding}
          vinData={vinData}
              setVinData={setVinData}
              handleDecodeVIN={handleDecodeVIN}
              showEditModal={showEditModal}
              setShowEditModal={setShowEditModal}
              editingCustomer={editingCustomer}
              setEditingCustomer={setEditingCustomer}
              handleEditSubmit={handleEditSubmit}
              showDetailModal={showDetailModal}
              setShowDetailModal={setShowDetailModal}
              detailCustomer={detailCustomer}
              handleViewCustomerDetail={handleViewCustomerDetail}
              handleEditCustomer={handleEditCustomer}
              handleDeleteCustomer={handleDeleteCustomer}
              handleScheduleAppointment={handleScheduleAppointment}
              handleAddTradeIn={handleAddTradeIn}
              handleDeleteTradeIn={handleDeleteTradeIn}
              schedulingCustomer={schedulingCustomer}
              setSchedulingCustomer={setSchedulingCustomer}
              handleAppointmentScheduled={handleAppointmentScheduled}
              showScheduleModal={showScheduleModal}
              setShowScheduleModal={setShowScheduleModal}
              showTradeInModal={showTradeInModal}
              setShowTradeInModal={setShowTradeInModal}
              tradeInForm={tradeInForm}
              setTradeInForm={setTradeInForm}
              handleTradeInSubmit={handleTradeInSubmit}
        importFile={importFile}
        setImportFile={setImportFile}
        importPreview={importPreview}
        setImportPreview={setImportPreview}
        columnMapping={columnMapping}
        setColumnMapping={setColumnMapping}
        isImporting={isImporting}
              handleFileUpload={handleFileUpload}
              handleBulkImport={handleBulkImport}
              fetchCustomers={fetchCustomers}
            />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
      <Footer />
    </>
  );
}

export default App;
